// src/RegulatoryCompliance.js
// =============================================================================
// CHANGE (this version): consistent "Data source" model across every branch
// drill-down dialog and every data element.
//   • Every element row has a DATA SOURCE column:
//       – periodic-event elements show an "Update" button (record a value with
//         a chosen date, posted to periodic-tasks/entries);
//       – attestable elements show the recorded source (Head office / Paper /
//         3rd party…) or "Set source" if none is recorded yet;
//       – live Pharmace-driven elements show a static "Head office" tag —
//         "Head office holds evidence" MEANS the data is in Pharmace (the
//         head-office data areas), so there is no separate "Pharmace (live
//         data)" option anywhere.
//   • The ▸ toggle column is headed "Review data" (opens the source/review
//     panel). The old trailing "State" column is removed — it only repeated
//     the data source; done/not-done is carried by the row colour and ✓/✗.
//   • Attestations are stored via compliance/attestations (backend
//     headoffice.js v42) and count as compliant. A live stream that answers —
//     even "not done" — always outranks an attestation, except for
//     ALT_OVERRIDES_LIVE elements where the recorded system choice wins.
// =============================================================================
// Regulatory Compliance dashboard.
//
// A four-level drill-down for tracking UK community-pharmacy regulatory
// compliance across the fleet:
//
//   GROUP (tab)  →  ITEM  →  BRANCH  →  DATA ELEMENT
//
//   • GROUP  — a compliance area, shown as a tab. The tab header carries a
//              rolled-up % (mean of its items) with a green→red background.
//   • ITEM   — a specific requirement within the area (e.g. "Responsible
//              Pharmacist record"). Listed inside the tab, each with its own
//              rolled-up % (mean across branches) and colour.
//   • BRANCH — a pharmacy. Clicking an item shows every branch with its % for
//              that item (fraction of the item's data elements that are "done"
//              at that branch).
//   • DATA ELEMENT — the actual checkable things. Clicking a branch shows the
//              elements, green (done) / red (not done). Each element also has:
//                – a requirement badge: Mandatory / Recommended / Optional
//                – floating help text on hover (the ⓘ icon)
//                – an optional external link to source guidance
//
// Percentages roll UP from the data elements, so the whole tree is consistent:
// a tab's number is the honest mean of what's underneath it.
//
// DATA IS PLACEHOLDER for now. Every data element currently gets a synthetic
// done/not-done from a stable hash of (item + branch + element) so the colours
// look realistic and the roll-ups are believable while we wire up real sources
// one element at a time. Search for `PLACEHOLDER` to find the seam where each
// element's real value will eventually be fetched.
//
// The compliance taxonomy (groups/items/elements) is grounded in:
//   • GPhC Standards for Registered Pharmacies — the five Principles
//     (governance, staff, premises, services/medicines, equipment).
//   • Statutory/legal duties that sit alongside the GPhC standards:
//     controlled drugs, information governance (DSPT/UK GDPR), health & safety,
//     and NHS Terms of Service / CPAF.
// These are a sensible working structure, not a substitute for the source
// documents — each item links out to the relevant guidance where available.
// =============================================================================

import React, { useMemo, useState, useEffect } from "react";
import { useMaskNames, displayName } from "./nameMask";
import PeriodicTaskEntryDialog from "./PeriodicTaskEntryDialog";
import NearMissPharmacyDialog from "./NearMissPharmacyDialog";

const API_BASE = "https://api.pharmacegroup.co.uk";

// Fetch the periodic-task grid and index it for the compliance views.
async function fetchTaskGrid(token) {
  const gres = await fetch(`${API_BASE}/api/headoffice/${withOrgScope("periodic-tasks/grid")}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!gres.ok) return null;
  const g = await gres.json();
  const rowsByName = {};
  for (const r of (g.rows || [])) if (r && r.pharmacyName) rowsByName[r.pharmacyName] = r;
  const dataTypeById = {}, optionsById = {}, frequencyById = {}, idByName = {};
  for (const t of (g.tasks || [])) {
    dataTypeById[String(t.taskId)] = t.dataType;
    optionsById[String(t.taskId)] = t.options || [];
    frequencyById[String(t.taskId)] = t.frequency || null;
    if (t.description) idByName[t.description] = String(t.taskId);
  }
  return { rowsByName, dataTypeById, optionsById, frequencyById, idByName };
}

// Record a periodic-task value for one branch, with a caller-chosen date.
async function postTaskEntry(token, { taskId, dbName, pharmacyId, value, recordedAt, note }) {
  const res = await fetch(`${API_BASE}/api/headoffice/periodic-tasks/entries`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ taskId, dbName, pharmacyId, value, recordedAt, note: note || null,
      recordedBy: localStorage.getItem("userName") || null }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
  return d;
}

async function postAttestation(token, path, body) {
  const res = await fetch(`${API_BASE}/api/headoffice/compliance/attestations/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
  return d;
}

// Data-source choices offered in the per-element dialog. "Head office holds
// evidence" means the data lives in Pharmace (the head-office panel data
// areas) — there is deliberately NO separate "Pharmace (live data)" option,
// because that would mean the same thing.
const ALT_SOURCES = ["Head office holds evidence", "Paper records", "Third-party system", "Other"];
// Short display label for a recorded alt source — shown on the row button and
// in the state column so the CHOICE is visible, not just a generic "Alt".
function shortAltLabel(source) {
  const sl = String(source || "").toLowerCase();
  if (sl.startsWith("paper")) return "Paper";
  if (sl.startsWith("third")) return "3rd party";
  if (sl.startsWith("head office")) return "Head office";
  if (!source) return "Source";
  return source.length > 12 ? source.slice(0, 12) + "…" : source;
}

// -----------------------------------------------------------------------------
// Organisation (member-group) scoping — mirrors HeadOffice.js.
//
// The viewer's organisation is stored in localStorage. When present, we pass it
// to the backend so the fleet fan-out is restricted to that group's member
// pharmacies. "Pharmace all" (admin) and an absent value both mean "whole
// fleet" server-side, so it is always safe to send.
//   - GET  endpoints: appended as ?organisationName=...
//   - POST endpoints: merged into the request body as { organisationName }.
// -----------------------------------------------------------------------------
function viewerOrg() {
  return localStorage.getItem("organisationName") || "";
}
function withOrgScope(path) {
  const org = viewerOrg();
  if (!org) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}organisationName=${encodeURIComponent(org)}`;
}
function withOrgBody(body) {
  const org = viewerOrg();
  return org ? { ...(body || {}), organisationName: org } : (body || {});
}


// -----------------------------------------------------------------------------
// Colour helpers (same green→red scheme used on the Head Office dashboard, so
// the visual language is consistent across the platform).
// -----------------------------------------------------------------------------

// Background for a 0–100 percentage: full green at 100, full red at <=10 (but
// >0), interpolated through amber between. Text stays dark for legibility.
function pctBg(pct) {
  if (pct == null || Number.isNaN(pct)) return "#eee";
  const t = Math.max(0, Math.min(1, (pct - 10) / 90));
  const hue = Math.round(120 * t); // 0 = red, 120 = green
  return `hsl(${hue}, 75%, 85%)`;
}
// Slightly stronger fill for the tab headers so they read as headers.
function pctBgStrong(pct) {
  if (pct == null || Number.isNaN(pct)) return "#ddd";
  const t = Math.max(0, Math.min(1, (pct - 10) / 90));
  const hue = Math.round(120 * t);
  return `hsl(${hue}, 70%, 72%)`;
}
const fmtPct = (n) => (n == null || Number.isNaN(n) ? "—" : `${Math.round(n)}%`);

// Modules that expose the 12-month effectiveness endpoint (percentage metrics).
const EFFECTIVENESS_MODULES = ["fridge", "cleaning", "date-checking", "sop"];

// Colour band for an effectiveness percentage against its threshold:
// green at/above threshold, amber within 10 points below, red beyond that.
function pctBand(pct, threshold) {
  if (pct == null) return { bg: "#f1f5f9", fg: "#64748b", label: "No data" };
  if (pct >= threshold) return { bg: "#dcfce7", fg: "#166534", label: "Meeting target" };
  if (pct >= threshold - 10) return { bg: "#fef9c3", fg: "#854d0e", label: "Below target" };
  return { bg: "#fee2e2", fg: "#991b1b", label: "Well below target" };
}


// -----------------------------------------------------------------------------
// Requirement-level badge. Mandatory (legal/regulatory must), Recommended
// (best practice / strongly expected), Optional (nice to have).
// -----------------------------------------------------------------------------
const REQ_META = {
  mandatory:   { label: "Mandatory",   bg: "#fee2e2", fg: "#991b1b", border: "#fca5a5" },
  recommended: { label: "Recommended", bg: "#fef3c7", fg: "#92400e", border: "#fcd34d" },
  optional:    { label: "Optional",    bg: "#e0e7ff", fg: "#3730a3", border: "#a5b4fc" },
};
function ReqBadge({ requirement }) {
  const m = REQ_META[requirement] || REQ_META.optional;
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        padding: "1px 6px",
        borderRadius: 4,
        background: m.bg,
        color: m.fg,
        border: `1px solid ${m.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {m.label}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Floating help. A small ⓘ that reveals a tooltip on hover/focus, with the
// explanation and (optionally) a "More info" external link.
// -----------------------------------------------------------------------------
function HelpInfo({ help, link }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // {top,left} in viewport coords
  const btnRef = React.useRef(null);
  if (!help && !link) return null;

  // Compute a fixed-position anchor from the button's rect so the tooltip is
  // never clipped by (or reflow-looped against) the scrolling modal frame.
  const show = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      // Prefer below; if too close to viewport bottom, place above.
      const below = r.bottom + 8;
      const placeAbove = below + 120 > window.innerHeight;
      setPos({
        top: placeAbove ? r.top - 8 : below,
        left: Math.min(Math.max(r.left + r.width / 2, 150), window.innerWidth - 150),
        above: placeAbove,
      });
    }
    setOpen(true);
  };
  const hide = () => setOpen(false);

  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <button
        ref={btnRef}
        type="button"
        aria-label="Help"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => { e.stopPropagation(); open ? hide() : show(); }}
        style={{
          width: 16, height: 16, lineHeight: "14px", padding: 0,
          borderRadius: "50%", border: "1px solid #94a3b8", background: "#f8fafc",
          color: "#475569", fontSize: 11, fontWeight: 700, cursor: "help",
        }}
      >
        i
      </button>
      {open && pos && (
        <span
          role="tooltip"
          style={{
            position: "fixed", zIndex: 2000,
            top: pos.top, left: pos.left,
            transform: pos.above ? "translate(-50%, -100%)" : "translateX(-50%)",
            width: 280,
            background: "#1f2937", color: "#f9fafb", fontSize: 12, lineHeight: 1.4,
            padding: "8px 10px", borderRadius: 6, boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
            fontWeight: 400, textTransform: "none", letterSpacing: 0,
            pointerEvents: "none",
          }}
        >
          {help}
          {link && (
            <span style={{ display: "block", marginTop: 6 }}>
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: "#93c5fd", textDecoration: "underline", pointerEvents: "auto" }}
              >
                More info ↗
              </a>
            </span>
          )}
        </span>
      )}
    </span>
  );
}

// -----------------------------------------------------------------------------
// PLACEHOLDER branch list. Eventually this comes from the fleet (userData /
// an API). For now a fixed set so the drill-down is demonstrable.
// -----------------------------------------------------------------------------

// Stable string hash → 0..1, so placeholder "done" states are deterministic
// (a given element at a given branch always shows the same colour between
// renders) but still look varied across the grid.
function hash01(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // map to 0..1
  return ((h >>> 0) % 1000) / 1000;
}

// =============================================================================
// LIVE DATA WIRING (2026-06)
//
// Some compliance elements are backed by real Head Office module endpoints.
// Each "fleet-status" endpoint returns one row per active pharmacy:
//   [{ pharmacyId, pharmacyName, status: 'ok'|'unchecked'|'fail', ... }]
// We fetch each needed endpoint ONCE, key the rows by pharmacyName, and map a
// branch's module status to done/not-done for the relevant element(s).
//
// Everything NOT in LIVE_SOURCES still falls back to placeholder values, so the
// board stays complete while we wire elements one at a time. An element with a
// live source shows a small "live" tag in the UI.
//
// Mapping note: a module's fleet-status is a single pass/fail per pharmacy, but
// a tab may have several elements under one module (e.g. fridge has daily-log,
// excursions, date-check). Where the endpoint only supports a coarse signal we
// map the module status onto the closest element and leave the finer ones on
// placeholder until a dedicated metric exists.
//
// status → done:  'ok' = done(true); 'fail'/'unchecked' = not done(false).
// =============================================================================

// element id  →  { endpoint, done(statusRow) }
const LIVE_SOURCES = {
  // NOTE: fridge "logged" + "in range" are no longer binary elements — they are
  // the two 12-month effectiveness metrics on the cold-chain item. The old
  // fridge/fleet-status point-in-time wiring was removed because it went red
  // simply when today's reading was missing, which isn't a compliance signal.

  // --- Cleaning ---
  "env-clean":     { endpoint: "cleaning/fleet-status",
                     done: (r) => r.status === "ok" },

  // --- CD register (status ok = no overdue balance checks / no open discrepancies) ---
  "cd-register":   { endpoint: "cd-register/fleet-status",
                     done: (r) => r.status === "ok" },

  // --- Handover diary (fleet-status: ok when nothing overdue) ---
  // Mapped to the CD-officer/handover governance proxy isn't right; handover
  // is its own concern. There's no handover element in the taxonomy yet, so
  // we surface it under the closest existing one only if added later.
  // (Endpoint available: handover/fleet-status.)

  // --- Patient safety / LFPSE (snapshot, per pharmacy) ---
  // done = no incomplete LFPSE entries outstanding.
  "lfpse":         { endpoint: "patient-safety/snapshot",
                     done: (r) => (r.lfpseIncompleteCount || 0) === 0 },

  // --- Accident book / RIDDOR (snapshot, per pharmacy) ---
  // "Accident book maintained" = the book is in active use (has entries). A
  // pharmacy with accident records is maintaining the book, regardless of
  // whether individual reviews are overdue (review timeliness is a separate
  // concern, surfaced via reviewOverdueCount if needed).
  "riddor-book":   { endpoint: "accident-book/snapshot",
                     done: (r) => !!r.hasEntries || (r.totalEntries || 0) > 0 },
  // "RIDDOR-reportable events reported" = no accident left awaiting a RIDDOR
  // decision (NOT_YET_KNOWN). Those are the genuine reporting-compliance risk.
  "riddor-report": { endpoint: "accident-book/snapshot",
                     done: (r) => (r.notYetKnownCount || 0) === 0 },

  // --- RP sign-in (snapshot, per pharmacy) ---
  // RP record kept: done if currently signed in, or RP activity recorded in
  // the period (daysCovered > 0).
  "rp-log":        { endpoint: "rp-signin/snapshot",
                     done: (r) => !!r.currentlySignedIn || (r.daysCovered || r.distinctDaysCovered || 0) > 0 },
  // NOTE: "rp-absence" (Absence rules followed, max 2h) is deliberately NOT
  // linked to the RP sign-in log — it is a periodic event, recorded via a
  // periodic-task def (see TASK_DEF_BY_NAME below).

  // --- SOP per-staff sign-off (sop_assignments) ---
  // done = no assignments overdue (everything assigned is signed or not yet due).
  "sop-signed":    { endpoint: "sop/sign-off-fleet-status",
                     done: (r) => r.status === "ok" || (r.overdueCount || 0) === 0 },

  // --- NHS Terms of Service: opening hours configured (organisations) ---
  // done = opening hours are recorded for the branch.
  "tos-hours":     { endpoint: "opening-hours/fleet-status",
                     done: (r) => !!r.hasOpeningHours },

  // --- Workforce (staffs + pharma_role_datas per tenant) ---
  // Adequate staffing: done if at least one active pharmacist on the books.
  "wb-staffing":   { endpoint: "workforce/fleet-status",
                     done: (r) => !!r.hasPharmacist },

  // --- MHRA safety alerts (mrha_safety_alert_logs per tenant) ---
  // done = no logged alert left without a recorded outcome.
  "sup-mhra":      { endpoint: "mhra/fleet-status",
                     done: (r) => (r.outstanding || 0) === 0 },
};

// Distinct endpoints we need to fetch, derived from LIVE_SOURCES.
const LIVE_ENDPOINTS = Array.from(new Set(Object.values(LIVE_SOURCES).map((s) => s.endpoint)));

// -----------------------------------------------------------------------------
// EXTRA live sources — POST endpoints that live at the API root (not under
// /api/headoffice) and return per-branch activity rather than a pass/fail
// status. We fetch each once, map its branch rows by pharmacyName, and derive
// done/not-done from activity. These come from routes.js, not headoffice.js.
//
// Each entry: { path, method, body, rowsOf(json), nameOf(row), done(row) }
//   path     — root-relative API path (POST)
//   rowsOf   — pull the per-branch array out of the response
//   nameOf   — branch name from a row
//   done     — compliance done/not-done for that branch
// keyed by a synthetic source id so multiple elements can share one fetch.
// -----------------------------------------------------------------------------
const EXTRA_SOURCES = {
  // Near-miss: branch is compliant on "log maintained" if it's actively using
  // the near-miss tool this month (isUsingTool) or has logged incidents.
  "nearmiss-grid": {
    path: "/near_miss_analysis_grid",
    body: {},
    rowsOf: (j) => j.branches || [],
    nameOf: (r) => r.pharmacyName,
    done: (r) => !!r.isUsingTool || (r.incidentsThisMonth || 0) > 0,
  },
  // NMS: branch is compliant on "NMS records" if it has any NMS activity data.
  "nms-analysis": {
    path: "/nms_analysis",
    body: {},
    rowsOf: (j) => j.data || [],
    nameOf: (r) => r.pharmacyName,
    done: (r) => Array.isArray(r.nms) && r.nms.length > 0,
  },
};

// compliance element id → EXTRA_SOURCES key
const EXTRA_FOR = {
  "near-miss": "nearmiss-grid",
  "adv-nms":   "nms-analysis",
};

// Some compliance elements map to a PRE-EXISTING periodic task by NAME rather
// than to one of the 45 imported defs (whose _ids we hold). For these we look
// up the task in the live grid's task list by description and treat a recorded
// value as evidence. EPS operational is evidenced by the monthly "Item
// submission" task (item count submitted for claiming = the branch is active
// on EPS). done = a value recorded in-date.
// element id → task description to match in the grid.
const TASK_DEF_BY_NAME = {
  "tos-eps": "Item submission",
  // Absence rules (max 2h) is a periodic event, not derived from the RP
  // sign-in log. Wire it to a periodic-task def with this exact description
  // (created in Periodic Tasks); until the def exists it stays on placeholder.
  // Alternatively paste its _id into TASK_DEF_FOR as "rp-absence".
  "rp-absence": "Absence rules followed (max 2h)",
};


// -----------------------------------------------------------------------------
// Periodic-task-backed elements.
//
// The 45 attestation elements are recorded as Periodic Task defs. Once a def
// exists, paste its _id (the $oid string) here against the compliance element.
// The dashboard then reads that task's value per branch from periodic-tasks/grid
// and treats a recorded "done"-ish value as complete.
//
// TASK_DEF_FOR: compliance element id → periodic_task_defs _id (string).
// Leave blank until the def is created; blank entries stay on placeholder.
//
// Example once created:
//   "indemnity": "6a1d22a2e534b456d409a75a",
// -----------------------------------------------------------------------------
const TASK_DEF_FOR = {
  // 45 compliance attestations → periodic_task_defs _id (imported 2026-06-29).
  "adv-dms": "6a426c6464255e9bfcfdfb08",
  "adv-pf": "6a426c6464255e9bfcfdfb07",
  "cal-cmp": "6a426c6464255e9bfcfdfaf0",
  "cal-scales": "6a426c6464255e9bfcfdfaef",
  "cal-thermometer": "6a426c6464255e9bfcfdfaee",
  "cd-officer": "6a426c6464255e9bfcfdfaeb",
  "coshh-assess": "6a426c6464255e9bfcfdfaff",
  "cpaf-gateway": "6a426c6464255e9bfcfdfb06",
  "cpaf-pages": "6a426c6464255e9bfcfdfb05",
  "cpaf-questionnaire": "6a426c6464255e9bfcfdfb04",
  "dspt-iao": "6a426c6464255e9bfcfdfaf6",
  "dspt-published": "6a426c6464255e9bfcfdfaf4",
  "dspt-training": "6a426c6464255e9bfcfdfaf5",
  "env-consultation": "6a426c6464255e9bfcfdfae6",
  "env-temperature": "6a426c6464255e9bfcfdfae7",
  "gdpr-breach": "6a426c6464255e9bfcfdfafa",
  "gdpr-ico": "6a426c6464255e9bfcfdfaf7",
  "gdpr-privacy": "6a426c6464255e9bfcfdfaf8",
  "gdpr-sar": "6a426c6464255e9bfcfdfaf9",
  "hs-elinsurance": "6a426c6464255e9bfcfdfafe",
  "hs-firstaid": "6a426c6464255e9bfcfdfafd",
  "hs-poster": "6a426c6464255e9bfcfdfafb",
  "hs-riskassess": "6a426c6464255e9bfcfdfafc",
  "indemnity": "6a426c6464255e9bfcfdfade",
  "it-backup": "6a426c6464255e9bfcfdfaf2",
  "it-encryption": "6a426c6464255e9bfcfdfaf3",
  "it-pmr-secure": "6a426c6464255e9bfcfdfaf1",
  "reg-pharmacist": "6a426c6464255e9bfcfdfadf",
  "reg-revalidation": "6a426c6464255e9bfcfdfae1",
  "reg-technician": "6a426c6464255e9bfcfdfae0",
  "rp-displayed": "6a426c6464255e9bfcfdfadc",
  "sec-cd-cabinet": "6a426c6464255e9bfcfdfae8",
  "sec-pmedicine": "6a426c6464255e9bfcfdfaea",
  "sec-pom-access": "6a426c6464255e9bfcfdfae9",
  "sop-dispensing": "6a426c6464255e9bfcfdfadd",
  "sup-distance": "6a426c6464255e9bfcfdfaed",
  "sup-frp": "6a426c6464255e9bfcfdfaec",
  "tos-disposal": "6a426c6464255e9bfcfdfb03",
  "tos-signposting": "6a426c6464255e9bfcfdfb02",
  "trn-counter": "6a426c6464255e9bfcfdfae2",
  "trn-cpd": "6a426c6464255e9bfcfdfae4",
  "trn-safeguarding": "6a426c6464255e9bfcfdfae3",
  "waste-contract": "6a426c6464255e9bfcfdfb01",
  "waste-segregation": "6a426c6464255e9bfcfdfb00",
  "wb-speakup": "6a426c6464255e9bfcfdfae5",
};

// Whether ANY element is task-backed yet (controls whether we fetch the grid).
const ANY_TASK_BACKED = Object.values(TASK_DEF_FOR).some(Boolean);

// Map a periodic-task grid cell to done/not-done. A cell looks like
// { value, recordedAt, state }. We treat it as done when it's in-date AND the
// recorded value is affirmative (boolean true / "yes", a non-empty date, or a
// non-empty/!="no" text/choice). Overdue or never-recorded = not done.
// BUG FIX 2026-07: the backend emits the in-date state as "ok" (see
// ptDueState in headoffice.js) — this previously tested for "in-date", so
// value-less in-date cells were wrongly counted as not done.
function taskCellDone(cell, dataType) {
  if (!cell) return false;
  if (cell.state === "overdue" || cell.state === "never") return false;
  if (cell.state === "na") return true; // not relevant = not counted against
  const inDate = cell.state === "ok" || cell.state === "in-date" || cell.state === "due";
  const v = cell.value;
  if (v == null || v === "") return inDate;
  if (dataType === "boolean") return v === true || v === "true" || v === "yes" || v === 1;
  if (dataType === "numeric") return Number(v) > 0;
  if (dataType === "choice")  return String(v).toLowerCase() !== "no" && String(v) !== "";
  return true; // date/text present
}


// PLACEHOLDER: synthetic done/not-done for one element at one branch.
// ~70% done on average so the board looks like a real, partly-complete fleet.
function placeholderDone(groupId, itemId, branchName, elementId) {
  return hash01(`${groupId}|${itemId}|${branchName}|${elementId}`) < 0.7;
}

// Resolve done/not-done for an element at a branch. Order of preference:
//   1. A direct module endpoint (LIVE_SOURCES)
//   2. An extra POST endpoint (EXTRA_FOR / extraData)
//   3. A periodic-task def (TASK_DEF_FOR) read from the grid
//   4. Placeholder
// Returns { done, live }.
// Elements where the alt-evidence choice is offered ALONGSIDE a live Pharmace
// stream (the "which system does this branch use?" selector). For these, a
// recorded attestation (paper / third-party) OUTRANKS the live data — the
// branch has declared it runs this record elsewhere, so the Pharmace figures
// are informational, not the compliance state.
const ALT_OVERRIDES_LIVE = new Set(["near-miss", "lfpse", "sup-mhra", "riddor-book", "riddor-report", "adv-nms", "tos-hours"]);

function resolveDone(groupId, itemId, branchName, elementId, liveData, taskGrid, extraData, attData) {
  // Attestation fallback: when this element has NO digital data stream for the
  // branch (not resolved by a live source or a recorded task), an active
  // "alternative evidence" attestation (paper records etc.) counts as done.
  // A live stream that answers — even "not done" — always outranks it,
  // EXCEPT for ALT_OVERRIDES_LIVE elements, where the recorded system choice
  // wins over the live stream.
  const att = attData && attData[branchName] && attData[branchName][elementId];
  if (att && ALT_OVERRIDES_LIVE.has(elementId)) {
    return { done: true, live: true, alt: att };
  }
  // Track whether this element is wired to a source that DID load — so a missing
  // branch row means "no data for this branch" (not done), not a fake placeholder.
  let wiredButNoRow = false;

  const src = LIVE_SOURCES[elementId];
  if (src) {
    if (liveData && liveData[src.endpoint]) {
      const row = liveData[src.endpoint][branchName];
      if (row) {
        try { return { done: !!src.done(row), live: true }; }
        catch { /* fall through */ }
      } else {
        wiredButNoRow = true; // endpoint loaded, but this branch wasn't in it
      }
    }
  }
  const exKey = EXTRA_FOR[elementId];
  if (exKey) {
    if (extraData && extraData[exKey]) {
      const ex = EXTRA_SOURCES[exKey];
      const row = extraData[exKey][branchName];
      if (row) {
        try { return { done: !!ex.done(row), live: true }; }
        catch { /* fall through */ }
      } else {
        wiredButNoRow = true;
      }
    }
  }
  const defId = TASK_DEF_FOR[elementId];
  if (defId) {
    if (taskGrid && taskGrid.rowsByName) {
      const row = taskGrid.rowsByName[branchName];
      const dataType = taskGrid.dataTypeById?.[defId];
      if (row && row.cells && row.cells[defId]) {
        return { done: taskCellDone(row.cells[defId], dataType), live: true };
      }
      // Task is wired; a missing cell means "never recorded" = not done.
      wiredButNoRow = true;
    }
  }
  // Name-matched pre-existing task (e.g. EPS → "Item submission").
  const taskName = TASK_DEF_BY_NAME[elementId];
  if (taskName) {
    if (taskGrid && taskGrid.idByName) {
      const tId = taskGrid.idByName[taskName];
      if (tId) {
        const row = taskGrid.rowsByName[branchName];
        const dataType = taskGrid.dataTypeById?.[tId];
        if (row && row.cells && row.cells[tId]) {
          return { done: taskCellDone(row.cells[tId], dataType), live: true };
        }
        wiredButNoRow = true;
      }
    }
  }

  // Wired to a real source, but no data for THIS branch → honestly "not done /
  // no data". This is the correct compliance signal (e.g. a pharmacy with no
  // accident-book activity), not a synthetic placeholder.
  if (wiredButNoRow) {
    if (att) return { done: true, live: true, alt: att };
    return { done: false, live: true, noData: true };
  }

  // Not wired to anything → attestation if present, else placeholder
  // (synthetic, clearly flagged in the UI).
  if (att) return { done: true, live: true, alt: att };
  return { done: placeholderDone(groupId, itemId, branchName, elementId), live: false };
}

// Is this element wired to a live source (module endpoint, extra POST, or task)?
function elementIsLive(elementId) {
  return !!LIVE_SOURCES[elementId] || !!EXTRA_FOR[elementId]
    || !!TASK_DEF_FOR[elementId] || !!TASK_DEF_BY_NAME[elementId];
}

// -----------------------------------------------------------------------------
// Source descriptor — for debugging/validation. For a given element, returns
// where the done/not-done value comes from: the API call, its parameters, the
// per-branch field consumed, and the rule that decides done. Used by the modal's
// expandable "source" panel. Returns null for placeholder-only elements.
// -----------------------------------------------------------------------------
// Per-element rule text (mirrors the done() lambdas above in plain English).
const RULE_TEXT = {
  // module fleet-status
  "env-clean":     "done = status === 'ok' (nothing overdue/unlogged)",
  "cd-register":   "done = status === 'ok' (no overdue balance checks / open discrepancies)",
  "lfpse":         "done = lfpseIncompleteCount === 0",
  "riddor-book":   "done = hasEntries (the accident book has entries / is in use)",
  "riddor-report": "done = notYetKnownCount === 0 (no accident awaiting a RIDDOR decision)",
  "rp-log":        "done = currentlySignedIn === true, OR daysCovered > 0 in the period",
  "rp-absence":    "done = the 'Absence rules followed (max 2h)' periodic task has a value recorded and in-date",
  "sop-signed":    "done = status === 'ok', OR overdueCount === 0 (all assignments signed or not yet due)",
  "tos-hours":     "done = hasOpeningHours === true (opening times recorded for the branch)",
  "wb-staffing":   "done = hasPharmacist === true (≥1 active pharmacist on the books)",
  "sup-mhra":      "done = outstanding === 0 (no logged alert without a recorded outcome)",
  // extra POST sources
  "near-miss":     "done = isUsingTool === true, OR incidentsThisMonth > 0",
  "adv-nms":       "done = nms array present and non-empty (branch has NMS activity)",
  // task-by-name
  "tos-eps":       "done = the 'Item submission' task has a value recorded and in-date",
};

function sourceInfo(elementId, taskGrid) {
  // 1. Module fleet-status endpoint
  const src = LIVE_SOURCES[elementId];
  if (src) {
    return {
      kind: "Module endpoint",
      method: "GET",
      path: `/api/headoffice/${src.endpoint}`,
      params: "Auth: Bearer JWT. Scope derived from token (org or whole fleet). No query params — returns one row per active pharmacy.",
      field: "Matched to this branch by pharmacyName.",
      rule: RULE_TEXT[elementId] || "(see done() in LIVE_SOURCES)",
    };
  }
  // 2. Extra POST source (near-miss / NMS)
  const exKey = EXTRA_FOR[elementId];
  if (exKey) {
    const ex = EXTRA_SOURCES[exKey];
    return {
      kind: "Activity endpoint (routes.js)",
      method: ex.method || "POST",
      path: ex.path,
      params: `Auth: Bearer JWT (resolveScopeFromToken). Body: ${JSON.stringify(ex.body || {})}. Returns per-branch rows.`,
      field: "Matched to this branch by pharmacyName.",
      rule: RULE_TEXT[elementId] || "(see done() in EXTRA_SOURCES)",
    };
  }
  // 3. Periodic task def by _id
  const defId = TASK_DEF_FOR[elementId];
  if (defId) {
    const dt = taskGrid?.dataTypeById?.[defId];
    return {
      kind: "Periodic task (by _id)",
      method: "GET",
      path: "/api/headoffice/periodic-tasks/grid",
      params: `taskId (def _id): ${defId}${dt ? ` · dataType: ${dt}` : ""}. Read from the grid cell for this task × branch.`,
      field: "Grid row matched by pharmacyName; cell keyed by taskId.",
      rule: "done = cell state is in-date AND value affirmative (boolean true/'yes', non-empty date, numeric > 0, or non-'no' choice/text). overdue/never = not done; na = excluded.",
    };
  }
  // 4. Periodic task by name (EPS)
  const taskName = TASK_DEF_BY_NAME[elementId];
  if (taskName) {
    const tId = taskGrid?.idByName?.[taskName];
    return {
      kind: "Periodic task (by name)",
      method: "GET",
      path: "/api/headoffice/periodic-tasks/grid",
      params: `Matched by task description "${taskName}"${tId ? ` → resolved taskId: ${tId}` : " (not yet resolved — task not found in grid)"}.`,
      field: "Grid row matched by pharmacyName; cell keyed by resolved taskId.",
      rule: RULE_TEXT[elementId] || "done = task value recorded and in-date.",
    };
  }
  // 5. Placeholder — no backend source yet.
  return {
    kind: "Placeholder (no live source)",
    method: "—",
    path: "(none)",
    params: "No API call. The value is generated deterministically from a hash of (group · item · branch · element) so the board renders a realistic mix.",
    field: "Not matched to any backend data.",
    rule: "done = hash(group|item|branch|element) < 0.7  →  ~70% appear done. NOT real compliance data.",
    placeholder: true,
  };
}


// =============================================================================
// COMPLIANCE TAXONOMY (placeholder values; structure is real)
//
//   groups[] → items[] → elements[]
//
// Each element: { id, label, requirement, help, link? }
// Branches are applied uniformly (every item is assessed at every branch).
// =============================================================================
const GPHC_PRINCIPLES = "https://www.pharmacyregulation.org/pharmacies/standards-and-guidance-registered-pharmacies";

const GROUPS = [
  // ---------------------------------------------------------------------------
  { id: "governance", name: "Governance & Risk", subtitle: "GPhC Principle 1",
    items: [
      { id: "rp-record", name: "Responsible Pharmacist record",
        elements: [
          { id: "rp-displayed", label: "RP notice displayed", requirement: "mandatory",
            help: "A notice showing the name and registration number of the Responsible Pharmacist must be conspicuously displayed while the pharmacy is operating.",
            link: "https://www.pharmacyregulation.org/pharmacies/responsible-pharmacist" },
          { id: "rp-log", label: "RP record kept (electronic/paper)", requirement: "mandatory",
            help: "A record identifying the RP in charge for each period must be kept and retained for at least 5 years." },
          { id: "rp-absence", label: "Absence rules followed (max 2h)", requirement: "mandatory",
            help: "The RP may be absent for up to a maximum of two hours during the pharmacy's business hours, with arrangements in place." },
        ] },
      { id: "sops", name: "Standard Operating Procedures",
        // SOP reporting uses the same coloured %-bar format as the fridge
        // metrics (2026-07): % of SOPs reviewed/in date, and % of assignments
        // read & signed — both live figures from the SOP module.
        metrics: [
          { id: "sop-reviewed", label: "SOPs reviewed / in date", module: "sop",
            field: "pctReviewed", thresholdField: "reviewedThreshold", metField: "reviewedMet",
            help: "Of all live SOPs at this pharmacy, the % currently in date (review/expiry date not passed; SOPs without an expiry recorded count as in date). SOPs are commonly reviewed every 2 years or on process change." },
          { id: "sop-read", label: "SOPs read & signed by staff", module: "sop",
            field: "pctRead", thresholdField: "readThreshold", metField: "readMet",
            help: "Of all live SOP assignments at this pharmacy, the % that staff have read and signed. Measures whether SOPs are actually being worked to, not just written." },
        ],
        elements: [
          { id: "sop-dispensing", label: "Dispensing SOP covers transposition checks", requirement: "recommended",
            help: "Good-practice SOPs include an accuracy/clinical check step and near-miss capture." },
        ] },
      { id: "risk", name: "Risk management & incidents",
        elements: [
          { id: "near-miss", label: "Near-miss log maintained", requirement: "mandatory",
            help: "Errors and near misses should be recorded, reviewed and learned from to reduce recurrence.",
            link: GPHC_PRINCIPLES },
          { id: "lfpse", label: "Patient safety incidents recorded (LFPSE)", requirement: "recommended",
            help: "Recording patient safety events via the national Learn from Patient Safety Events service supports learning and is increasingly expected." },
          { id: "complaints", label: "Complaints procedure in place", requirement: "mandatory",
            help: "An accessible complaints procedure must be available, with complaints recorded and acted on." },
          { id: "indemnity", label: "Professional indemnity in force", requirement: "mandatory",
            help: "Appropriate professional indemnity / insurance cover is a legal requirement for providing pharmacy services." },
        ] },
    ] },

  // ---------------------------------------------------------------------------
  { id: "staff", name: "Staff & Training", subtitle: "GPhC Principle 2",
    items: [
      { id: "registration", name: "Professional registration",
        elements: [
          { id: "reg-pharmacist", label: "Pharmacist(s) GPhC registration current", requirement: "mandatory",
            help: "All pharmacists must hold current GPhC registration; revalidation must be up to date.",
            link: "https://www.pharmacyregulation.org/registration" },
          { id: "reg-technician", label: "Technician(s) GPhC registration current", requirement: "mandatory",
            help: "Registered pharmacy technicians must also hold current GPhC registration and complete revalidation." },
          { id: "reg-revalidation", label: "Revalidation records submitted", requirement: "mandatory",
            help: "Each registrant must submit CPD records, a peer discussion and reflective account annually." },
        ] },
      { id: "training", name: "Role-based training",
        elements: [
          { id: "trn-counter", label: "Counter staff medicines-counter trained", requirement: "mandatory",
            help: "Staff selling medicines must be trained (or enrolled within 3 months) to an accredited course for their role." },
          { id: "trn-safeguarding", label: "Safeguarding training (level appropriate)", requirement: "mandatory",
            help: "Staff should hold safeguarding training at a level appropriate to their role to recognise and act on concerns." },
          { id: "trn-cpd", label: "Ongoing CPD evidenced", requirement: "recommended",
            help: "Continuing professional development should be evidenced across the team, not just registrants." },
        ] },
      { id: "wellbeing", name: "Team & whistleblowing",
        elements: [
          { id: "wb-speakup", label: "Staff can raise concerns (speak-up route)", requirement: "mandatory",
            help: "Staff must be able to raise concerns and have them taken seriously and acted upon." },
          { id: "wb-staffing", label: "Adequate staffing for safe service", requirement: "mandatory",
            help: "There must be enough suitably trained staff to provide services safely and effectively." },
        ] },
    ] },

  // ---------------------------------------------------------------------------
  { id: "premises", name: "Premises", subtitle: "GPhC Principle 3",
    items: [
      { id: "environment", name: "Environment & cleanliness",
        metrics: [
          { id: "cleaning-ontime", label: "Cleaning done on time", module: "cleaning",
            field: "pctOnTimeYear", thresholdField: "onTimeThreshold", metField: "onTimeMet",
            help: "Of all scheduled cleans across all areas over the last 12 months, the % completed on time. Measures whether the cleaning schedule is actually being kept." },
        ],
        elements: [
          { id: "env-clean", label: "Cleaning log up to date", requirement: "mandatory",
            help: "The premises must be clean, safe and suitable; a cleaning record evidences ongoing upkeep." },
          { id: "env-consultation", label: "Consultation room available & private", requirement: "mandatory",
            help: "A consultation room is required for many NHS services and to allow private conversations." },
          { id: "env-temperature", label: "Premises temperature suitable", requirement: "recommended",
            help: "The environment should keep medicines and people comfortable and within safe ranges." },
        ] },
      { id: "security", name: "Security & access",
        elements: [
          { id: "sec-cd-cabinet", label: "CD cabinet compliant & secure", requirement: "mandatory",
            help: "Schedule 2 (and some Schedule 3) CDs must be stored in a cabinet meeting the Misuse of Drugs (Safe Custody) Regulations." },
          { id: "sec-pom-access", label: "POM areas access-controlled", requirement: "mandatory",
            help: "Access to medicines must be restricted to authorised staff." },
          { id: "sec-pmedicine", label: "P-medicine display risk-assessed", requirement: "recommended",
            help: "Self-selection or open display of P-medicines should be risk-assessed per GPhC FAQs." },
        ] },
    ] },

  // ---------------------------------------------------------------------------
  { id: "medicines", name: "Medicines & Services", subtitle: "GPhC Principle 4",
    items: [
      { id: "cd", name: "Controlled drugs",
        elements: [
          { id: "cd-register", label: "CD register balances reconciled", requirement: "mandatory",
            help: "Running balances in the CD register should be kept and periodically checked against physical stock.",
            link: "https://www.cqc.org.uk/guidance-providers/controlled-drugs" },
          { id: "cd-destruction", label: "CD destruction recorded (denatured)", requirement: "mandatory",
            help: "Destruction of CDs must be recorded; Schedule 2 destruction requires an authorised witness." },
          { id: "cd-sop", label: "CD SOPs current", requirement: "mandatory",
            help: "Standard operating procedures specific to CD management must be in place and followed." },
          { id: "cd-officer", label: "CD Accountable Officer notified of incidents", requirement: "mandatory",
            help: "CD-related concerns and incidents should be reported to the CD Accountable Officer." },
        ] },
      { id: "cold-chain", name: "Cold chain & storage",
        // Effectiveness metrics (12-month process measures), shown as coloured
        // % bars. These answer "is the process working over the year?", not
        // "was it OK yesterday". Fridge has TWO (frequency + in range); date-
        // checking has ONE consistency metric (its summary stores only activity
        // counts, no expected/scheduled denominator, so a true on-time % can't
        // be derived — we measure how consistently it's done instead).
        metrics: [
          { id: "fridge-checked", label: "Fridge checked on time", module: "fridge",
            field: "pctOnTimeYear", thresholdField: "onTimeThreshold", metField: "onTimeMet",
            help: "Of the days the pharmacy was open over the last 12 months, the % on which the fridge temperature was recorded. Measures whether the checking process is actually happening." },
          { id: "fridge-inrange", label: "Fridge temperature in range", module: "fridge",
            field: "pctInRangeYear", thresholdField: "inRangeThreshold", metField: "inRangeMet",
            help: "Of all temperature readings over the last 12 months, the % that were within 2–8°C. Measures whether cold-chain integrity is being maintained." },
          { id: "date-check-active", label: "Date-checking done consistently", module: "date-checking",
            field: "pctOnTimeYear", thresholdField: "onTimeThreshold", metField: "onTimeMet",
            help: "The % of the last 12 months in which date-checking was actually carried out. The date-checking record stores activity, not a schedule, so this measures consistency (is it done every month?) rather than per-shelf timeliness." },
        ],
        elements: [
          // Fridge (logged + in range) and date-checking are now represented by
          // the 12-month effectiveness metrics above, which use history rather
          // than today's reading.
        ] },
      { id: "supply", name: "Safe & effective supply",
        elements: [
          { id: "sup-mhra", label: "MHRA drug/safety alerts actioned", requirement: "mandatory",
            help: "Drug alerts and recalls must be acted on promptly and the action recorded." },
          { id: "sup-frp", label: "Pharmacy First clinical pathways followed", requirement: "recommended",
            help: "Where commissioned, services must follow the relevant clinical protocol/PGD." },
          { id: "sup-gp-notify", label: "GP notified of relevant supplies", requirement: "recommended",
            help: "Sharing supply information with the patient's GP supports continuity of care." },
          { id: "sup-distance", label: "Distance-selling safeguards (if applicable)", requirement: "optional",
            help: "Online/distance services must meet the GPhC distance-selling guidance, including high-risk medicine safeguards.",
            link: "https://assets.pharmacyregulation.org/files/2025-02/gphc-guidance-registered-pharmacies-providing-pharmacy-services-distance-february-2025.pdf" },
        ] },
    ] },

  // ---------------------------------------------------------------------------
  { id: "equipment", name: "Equipment & Facilities", subtitle: "GPhC Principle 5",
    items: [
      { id: "calibration", name: "Calibration & maintenance",
        elements: [
          { id: "cal-thermometer", label: "Fridge thermometer calibrated", requirement: "recommended",
            help: "Temperature-monitoring equipment should be accurate and periodically verified." },
          { id: "cal-scales", label: "Measuring/weighing equipment fit for purpose", requirement: "recommended",
            help: "Equipment used in preparation must be suitable, clean and maintained." },
          { id: "cal-cmp", label: "Compliance-aid sealing equipment maintained", requirement: "optional",
            help: "Where MDS/compliance aids are prepared, sealing equipment should be maintained." },
        ] },
      { id: "it-systems", name: "Clinical IT & continuity",
        elements: [
          { id: "it-pmr-secure", label: "PMR access controlled (smartcards)", requirement: "mandatory",
            help: "Clinical systems must restrict access to authorised users and protect patient information." },
          { id: "it-backup", label: "Business continuity plan in place", requirement: "mandatory",
            help: "Since 2023 NHS England requires a business continuity plan for each premises.",
            link: "https://www.england.nhs.uk/" },
          { id: "it-encryption", label: "Devices encrypted", requirement: "recommended",
            help: "End-user devices holding patient data should be encrypted." },
        ] },
    ] },

  // ---------------------------------------------------------------------------
  { id: "ig", name: "Information Governance", subtitle: "DSPT / UK GDPR",
    items: [
      { id: "dspt", name: "Data Security & Protection Toolkit",
        elements: [
          { id: "dspt-published", label: "DSPT published & up to standard", requirement: "mandatory",
            help: "Pharmacies must complete and publish the annual DSPT to access NHS systems.",
            link: "https://www.dsptoolkit.nhs.uk/" },
          { id: "dspt-training", label: "Annual data-security training completed", requirement: "mandatory",
            help: "All staff handling patient data must complete annual data-security awareness training." },
          { id: "dspt-iao", label: "Information asset owner assigned", requirement: "recommended",
            help: "Naming an information asset owner supports accountability for data handling." },
        ] },
      { id: "gdpr", name: "UK GDPR / data protection",
        elements: [
          { id: "gdpr-ico", label: "ICO registration (data protection fee) paid", requirement: "mandatory",
            help: "Organisations processing personal data must pay the ICO data protection fee unless exempt.",
            link: "https://ico.org.uk/" },
          { id: "gdpr-privacy", label: "Privacy notice available to patients", requirement: "mandatory",
            help: "A privacy notice explaining how personal data is used must be accessible to patients." },
          { id: "gdpr-sar", label: "Subject access request process defined", requirement: "recommended",
            help: "There should be a defined process to handle subject access requests within statutory timescales." },
          { id: "gdpr-breach", label: "Breach reporting route understood", requirement: "mandatory",
            help: "Reportable breaches must be notified to the ICO within 72 hours where required." },
        ] },
    ] },

  // ---------------------------------------------------------------------------
  { id: "hs", name: "Health & Safety", subtitle: "Statutory duties",
    items: [
      { id: "hs-core", name: "Workplace health & safety",
        elements: [
          { id: "hs-poster", label: "H&S law poster displayed / leaflet issued", requirement: "mandatory",
            help: "Employers must display the HSE health & safety law poster or provide the equivalent leaflet.",
            link: "https://www.hse.gov.uk/" },
          { id: "hs-riskassess", label: "Risk assessments current", requirement: "mandatory",
            help: "Suitable and sufficient risk assessments must be in place and reviewed." },
          { id: "hs-firstaid", label: "First aid provision adequate", requirement: "mandatory",
            help: "Adequate first-aid equipment, facilities and trained personnel must be provided." },
          { id: "hs-elinsurance", label: "Employers' liability insurance displayed", requirement: "mandatory",
            help: "Employers' liability insurance is a legal requirement and the certificate should be available." },
        ] },
      { id: "coshh", name: "COSHH & waste",
        elements: [
          { id: "coshh-assess", label: "COSHH assessments for hazardous substances", requirement: "mandatory",
            help: "Substances hazardous to health must be assessed and controlled under COSHH." },
          { id: "waste-segregation", label: "Pharmaceutical/clinical waste segregated", requirement: "mandatory",
            help: "Waste must be segregated (pharmaceutical, clinical, hazardous) and handled correctly." },
          { id: "waste-contract", label: "Licensed waste carrier contract in place", requirement: "mandatory",
            help: "Waste must be removed by a licensed carrier, with consignment/transfer notes retained." },
        ] },
      { id: "riddor", name: "Accidents & RIDDOR",
        elements: [
          { id: "riddor-book", label: "Accident book maintained", requirement: "mandatory",
            help: "Workplace accidents and injuries must be recorded." },
          { id: "riddor-report", label: "RIDDOR-reportable events reported", requirement: "mandatory",
            help: "Certain injuries, diseases and dangerous occurrences must be reported to the HSE under RIDDOR.",
            link: "https://www.hse.gov.uk/riddor/" },
        ] },
    ] },

  // ---------------------------------------------------------------------------
  { id: "nhs", name: "NHS Terms of Service", subtitle: "CPCF / CPAF",
    items: [
      { id: "essential", name: "Essential services & ToS",
        elements: [
          { id: "tos-hours", label: "Core & supplementary hours met", requirement: "mandatory",
            help: "Contractual opening hours must be maintained; unplanned closures must be managed and reported." },
          { id: "tos-eps", label: "EPS operational", requirement: "mandatory",
            help: "Electronic Prescription Service must be available as part of essential services." },
          { id: "tos-signposting", label: "Signposting & support for self-care", requirement: "mandatory",
            help: "Essential services include signposting and self-care support." },
          { id: "tos-disposal", label: "Unwanted medicines disposal offered", requirement: "mandatory",
            help: "Pharmacies must accept and dispose of patients' unwanted medicines." },
        ] },
      { id: "cpaf", name: "CPAF assurance",
        elements: [
          { id: "cpaf-questionnaire", label: "Annual CPAF questionnaire completed", requirement: "mandatory",
            help: "The Community Pharmacy Assurance Framework questionnaire must be completed when required.",
            link: "https://www.nhsbsa.nhs.uk/" },
          { id: "cpaf-pages", label: "NHS website profile (DoS) accurate", requirement: "recommended",
            help: "The pharmacy's NHS profile / Directory of Services entry should be accurate and current." },
          { id: "cpaf-gateway", label: "NHS gateway criteria evidenced", requirement: "recommended",
            help: "Be able to evidence the gateway/quality criteria sampled under CPAF." },
        ] },
      { id: "pharmacy-first", name: "Advanced services governance",
        elements: [
          { id: "adv-pf", label: "Pharmacy First requirements met", requirement: "recommended",
            help: "Where signed up, advanced service requirements (premises, training, pathways) must be met." },
          { id: "adv-nms", label: "NMS consent & records compliant", requirement: "recommended",
            help: "New Medicine Service requires valid consent and complete records." },
          { id: "adv-dms", label: "Discharge Medicines Service actioned", requirement: "optional",
            help: "DMS referrals from NHS trusts should be actioned within the service specification." },
        ] },
    ] },
];

// =============================================================================
// Roll-up maths. Everything derives from data elements so the tree is honest.
// =============================================================================

// % of an item's elements that are "done" at a given branch.
function itemBranchPct(group, item, branch, liveData, taskGrid, extraData, effData, attData) {
  const els = item.elements || [];
  const metrics = item.metrics || [];
  const total = els.length + metrics.length;
  if (total === 0) return null;
  let done = 0;
  for (const el of els) {
    if (resolveDone(group.id, item.id, branch, el.id, liveData, taskGrid, extraData, attData).done) done++;
  }
  // Each effectiveness metric counts as one pass/fail signal: pass = it meets
  // its threshold over the 12 months (backend-computed onTimeMet / inRangeMet).
  for (const m of metrics) {
    const row = effData?.[m.module]?.[branch];
    if (row && row[m.metField]) done++;
  }
  return (done / total) * 100;
}

// Item % across the whole fleet = mean of its per-branch %s.
function itemPct(group, item, branches, liveData, taskGrid, extraData, effData, attData) {
  if (branches.length === 0) return null;
  const vals = branches.map((b) => itemBranchPct(group, item, b, liveData, taskGrid, extraData, effData, attData)).filter((v) => v != null);
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// Group (tab) % = mean of its item %s.
function groupPct(group, branches, liveData, taskGrid, extraData, effData, attData) {
  const vals = (group.items || []).map((it) => itemPct(group, it, branches, liveData, taskGrid, extraData, effData, attData)).filter((v) => v != null);
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// =============================================================================
// Component
// =============================================================================
// -----------------------------------------------------------------------------
// Shared data fetch — the SAME loader feeds the main compliance board and the
// printable single-pharmacy report (CompliancePrintReport), so the two can
// never drift apart. Returns { liveData, extraData, effData, taskGrid,
// attData, liveBranches }; optional sources come back null on failure.
// -----------------------------------------------------------------------------
async function fetchAllComplianceData(token) {
  // A single broken module must never blank the whole Regulatory Compliance
  // board. Load each live source independently and keep the successful ones.
  // The organisation members list is the branch source-of-truth; these feeds
  // only provide evidence/status for those branches.
  const liveFailures = [];
  const results = await Promise.all(
    LIVE_ENDPOINTS.map(async (ep) => {
      try {
        const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(ep)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const rows = Array.isArray(json) ? json : (json.rows || json.results || []);
        return [ep, rows];
      } catch (e) {
        liveFailures.push(`${ep}: ${e.message || e}`);
        return [ep, []];
      }
    })
  );
  const byEndpoint = {};
  const nameSet = new Set();
  for (const [ep, rows] of results) {
    const byName = {};
    for (const r of rows) {
      if (r && r.pharmacyName) { byName[r.pharmacyName] = r; nameSet.add(r.pharmacyName); }
    }
    byEndpoint[ep] = byName;
  }

  // Extra POST sources (root-base routes.js endpoints: near-miss, NMS).
  const extra = {};
  const extraKeys = Object.keys(EXTRA_SOURCES);
  if (extraKeys.length) {
    await Promise.all(extraKeys.map(async (key) => {
      const cfg = EXTRA_SOURCES[key];
      try {
        const res = await fetch(`${API_BASE}${cfg.path}`, {
          method: cfg.method || "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(withOrgBody(cfg.body || {})),
        });
        if (!res.ok) return;
        const json = await res.json();
        const byName = {};
        for (const r of (cfg.rowsOf(json) || [])) {
          const nm = cfg.nameOf(r);
          if (nm) { byName[nm] = r; nameSet.add(nm); }
        }
        extra[key] = byName;
      } catch { /* this source optional */ }
    }));
  }

  // 12-month effectiveness metrics (fridge, cleaning).  Organisation
  // members store the canonical tenant dbName in members[].pharmacyId, while
  // these endpoints historically returned the pharmacy's common-db display
  // name.  Build an alias map so a branch called "Queenborough" in the org
  // can consume a row whose common-db name is "Tyrell & Jones (Queenborough)".
  const memberNameByDbName = new Map();
  try {
    const members = JSON.parse(localStorage.getItem("members") || "[]");
    for (const m of (Array.isArray(members) ? members : [])) {
      const dbName = m && m.pharmacyId != null ? String(m.pharmacyId) : "";
      const display = (m && (m.pharmacyName || m.name)) || "";
      if (dbName && display) memberNameByDbName.set(dbName, display);
    }
  } catch { /* no local membership cache yet */ }

  const eff = {};
  await Promise.all(EFFECTIVENESS_MODULES.map(async (mod) => {
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(`compliance/effectiveness?module=${mod}&months=12`)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const json = await res.json();
      const rows = Array.isArray(json) ? json : (json.rows || []);
      const byName = {};
      for (const r of rows) {
        if (!r) continue;
        if (r.pharmacyName) {
          byName[r.pharmacyName] = r;
          nameSet.add(r.pharmacyName);
        }
        // Canonical identity join: backend returns dbName for effectiveness
        // rows.  Index the same row under the organisation member's chosen
        // display name as well, so presentation names never break the data join.
        const alias = r.dbName != null ? memberNameByDbName.get(String(r.dbName)) : null;
        if (alias) {
          byName[alias] = r;
          nameSet.add(alias);
        }
      }
      eff[mod] = byName;
    } catch { /* optional */ }
  }));

  // Periodic-task grid (only if any element is task-backed).
  let taskGrid = null;
  if (ANY_TASK_BACKED) {
    try {
      const g = await fetchTaskGrid(token);
      if (g) {
        for (const n of Object.keys(g.rowsByName)) nameSet.add(n);
        taskGrid = g;
      }
    } catch { /* grid optional */ }
  }

  // Alternative-evidence attestations (paper records etc.).
  let attData = null;
  try {
    const ares = await fetch(`${API_BASE}/api/headoffice/${withOrgScope("compliance/attestations")}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (ares.ok) {
      const a = await ares.json();
      attData = a.byBranch || {};
    }
  } catch { /* attestations optional */ }

  return {
    liveData: byEndpoint,
    extraData: Object.keys(extra).length ? extra : null,
    effData: Object.keys(eff).length ? eff : null,
    taskGrid,
    attData,
    liveBranches: nameSet.size ? Array.from(nameSet).sort() : null,
    liveFailures,
  };
}

export default function RegulatoryCompliance({ token, userData }) {
  // "Hide names" admin toggle — display-only. All internal data joins key on
  // the REAL pharmacyName; masking is applied only at render points.
  const masked = useMaskNames();
  const [activeGroupId, setActiveGroupId] = useState(GROUPS[0].id);
  // Items start EXPANDED (2026-07): collapsedItemIds tracks the ones the user
  // has closed; switching group re-expands everything. drillItemId records
  // which item a clicked branch belongs to (feeds the drill-down modal) now
  // that several items can be open at once.
  const [collapsedItemIds, setCollapsedItemIds] = useState(() => new Set());
  const [drillItemId, setDrillItemId] = useState(null);
  const [openBranch, setOpenBranch] = useState(null);     // branch drilled into

  // liveData: { [endpoint]: { [pharmacyName]: statusRow } }. Fetched once.
  const [liveData, setLiveData] = useState(null);
  const [extraData, setExtraData] = useState(null); // { [extraKey]: { [pharmacyName]: row } }
  const [effData, setEffData] = useState(null); // { [module]: { [pharmacyName]: row } } 12-month effectiveness
  const [taskGrid, setTaskGrid] = useState(null); // { rowsByName, dataTypeById }
  const [attData, setAttData] = useState(null);   // { [pharmacyName]: { [elementId]: att } } — alternative-evidence attestations
  const [liveBranches, setLiveBranches] = useState(null); // real branch names from live data
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLiveLoading(true); setLiveError("");
      try {
        const d = await fetchAllComplianceData(token);
        if (cancelled) return;
        setLiveData(d.liveData);
        if (d.extraData) setExtraData(d.extraData);
        if (d.effData) setEffData(d.effData);
        if (d.taskGrid) setTaskGrid(d.taskGrid);
        if (d.attData) setAttData(d.attData);
        setLiveBranches(d.liveBranches);
        // Non-fatal: show a warning while retaining all successful modules.
        if (d.liveFailures && d.liveFailures.length) {
          setLiveError(`Some compliance feeds could not be loaded (${d.liveFailures.length}). Available data is still shown.`);
        }
      } catch (e) {
        if (!cancelled) { setLiveError(String(e.message || e)); }
      } finally {
        if (!cancelled) setLiveLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Branch identity comes from the organisation membership, not from whichever
  // compliance endpoint happened to return a row. This keeps all legitimate
  // branches visible even when a module has no data (or one module is down).
  // Only the fleet-wide admin view is derived from live results.
  const branches = useMemo(() => {
    const org = viewerOrg();
    if (org === "Pharmace all") {
      return liveBranches || (liveLoading ? [] : []);
    }
    let members = [];
    try { members = JSON.parse(localStorage.getItem("members") || "[]"); } catch { members = []; }
    const names = (Array.isArray(members) ? members : [])
      .map((m) => (m && (m.pharmacyName || m.name || m.pharmacyId)) || "")
      .filter(Boolean);
    // If membership has not made it into localStorage yet, use scoped live
    // results as a temporary fallback; never invent placeholder pharmacies.
    if (names.length === 0) return liveBranches || [];
    return [...new Set(names)].sort((a, b) => String(a).localeCompare(String(b)));
  }, [liveBranches, liveLoading]);

  const activeGroup = useMemo(
    () => GROUPS.find((g) => g.id === activeGroupId) || GROUPS[0],
    [activeGroupId]
  );

  // The item the drill-down modal is showing (resolved from the drill item id).
  const openItem = useMemo(
    () => (activeGroup.items || []).find((it) => it.id === drillItemId) || null,
    [activeGroup, drillItemId]
  );

  // Pre-compute tab %s (recompute when live data or branches change).
  const groupPcts = useMemo(() => {
    const m = {};
    for (const g of GROUPS) m[g.id] = groupPct(g, branches, liveData, taskGrid, extraData, effData, attData);
    return m;
  }, [branches, liveData, taskGrid, extraData, effData, attData]);

  return (
    <div style={{ padding: "16px 20px", maxWidth: 1180 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Regulatory Compliance</h1>
          <div style={{ color: "#64748b", fontSize: 13, marginTop: 2 }}>
            Fleet compliance across GPhC standards and statutory duties.
            Percentages roll up from individual data elements.
          </div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            {liveLoading
              ? <span style={{ color: "#92400e" }}>Please wait while loading branches…</span>
              : liveError
                ? <span style={{ color: "#991b1b" }}>{liveError} Organisation branches remain visible; affected elements show no live evidence.</span>
                : liveBranches
                  ? <span style={{ color: "#166534" }}>● Live data loaded. Showing {branches.length} organisation branches.</span>
                  : <span style={{ color: "#94a3b8" }}>No live evidence returned. Showing organisation branches.</span>}
          </div>
          <style>{`@keyframes rcSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
        <Legend />
      </div>

      {/* ---- Tabs (groups) ---- */}
      <div
        style={{
          display: "flex", flexWrap: "wrap", gap: 6, marginTop: 16,
          borderBottom: "2px solid #e2e8f0", paddingBottom: 0,
        }}
      >
        {GROUPS.map((g) => {
          const pct = groupPcts[g.id];
          const active = g.id === activeGroupId;
          return (
            <button
              key={g.id}
              onClick={() => { setActiveGroupId(g.id); setCollapsedItemIds(new Set()); setDrillItemId(null); setOpenBranch(null); }}
              title={`${g.name} — ${fmtPct(pct)} complete`}
              style={{
                outline: activeGroupId === g.id ? "2px solid #000" : "none", outlineOffset: 2,
                position: "relative", border: "1px solid #cbd5e1",
                borderBottom: active ? "2px solid #1e293b" : "1px solid #cbd5e1",
                borderTopLeftRadius: 8, borderTopRightRadius: 8,
                background: pctBgStrong(pct), padding: "8px 12px",
                cursor: "pointer", fontWeight: active ? 700 : 500,
                color: "#0f172a", marginBottom: -2,
                boxShadow: active ? "0 -2px 6px rgba(0,0,0,0.06)" : "none",
              }}
            >
              <div style={{ fontSize: 13, lineHeight: 1.1 }}>{g.name}</div>
              <div style={{ fontSize: 11, opacity: 0.7 }}>{g.subtitle}</div>
              <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>{fmtPct(pct)}</div>
            </button>
          );
        })}
      </div>

      {/* ---- Tab body: item list ---- */}
      <div style={{ marginTop: 18 }}>
        <h2 style={{ fontSize: 16, margin: "0 0 8px" }}>
          {activeGroup.name} <span style={{ color: "#94a3b8", fontWeight: 400 }}>· {activeGroup.subtitle}</span>
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
          {(activeGroup.items || []).map((item) => {
            const pct = itemPct(activeGroup, item, branches, liveData, taskGrid, extraData, effData, attData);
            const isOpen = !collapsedItemIds.has(item.id);
            const anyLive = (item.elements || []).some((el) => elementIsLive(el.id));
            return (
              <div key={item.id} style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                {/* Item header row */}
                <button
                  onClick={() => {
                    setCollapsedItemIds(prev => {
                      const next = new Set(prev);
                      if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
                      return next;
                    });
                  }}
                  style={{
                    outline: isOpen ? "2px solid #000" : "none", outlineOffset: -2,
                    width: "100%", display: "flex", alignItems: "center", gap: 12,
                    padding: "10px 12px", background: "#fff", border: "none",
                    cursor: "pointer", textAlign: "left", color: "#0f172a",
                  }}
                >
                  <span style={{ fontSize: 12, color: "#64748b", width: 14 }}>{isOpen ? "▼" : "▶"}</span>
                  <span style={{ flex: 1, fontWeight: 600, color: "#0f172a", fontSize: 14 }}>{item.name}</span>
                  {anyLive && <LiveTag />}
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>
                    {(item.elements || []).length} element{(item.elements || []).length === 1 ? "" : "s"}
                  </span>
                  <PctPill pct={pct} />
                </button>

                {/* Branch list for this item */}
                {isOpen && (
                  <div style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0", padding: "8px 12px 12px" }}>
                    <div style={{ fontSize: 12, color: "#64748b", margin: "2px 0 8px" }}>
                      Completion by branch — click a branch to see the data elements.
                    </div>
                    {liveLoading && branches.length === 0 ? (
                      <div style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "18px 12px", color: "#64748b", fontSize: 13,
                      }}>
                        <span
                          aria-hidden
                          style={{
                            width: 14, height: 14, borderRadius: "50%",
                            border: "2px solid #cbd5e1", borderTopColor: "#64748b",
                            display: "inline-block", animation: "rcSpin 0.8s linear infinite",
                          }}
                        />
                        Please wait while loading branches…
                      </div>
                    ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 6 }}>
                      {branches.map((b) => {
                        const bPct = itemBranchPct(activeGroup, item, b, liveData, taskGrid, extraData, effData, attData);
                        const bOpen = openBranch === b && drillItemId === item.id;
                        return (
                          <button
                            key={b}
                            onClick={() => {
                              setDrillItemId(item.id);
                              setOpenBranch((bOpen && drillItemId === item.id) ? null : b);
                            }}
                            title={`${displayName(b, masked)} — ${fmtPct(bPct)}`}
                            style={{
                              outline: bOpen ? "2px solid #000" : "none", outlineOffset: 1,
                              display: "flex", alignItems: "center", justifyContent: "space-between",
                              gap: 8, padding: "8px 10px", borderRadius: 6,
                              border: bOpen ? "2px solid #1e293b" : "1px solid #cbd5e1",
                              background: pctBg(bPct), cursor: "pointer", textAlign: "left",
                              boxShadow: bOpen ? "0 0 0 2px rgba(30,41,59,0.15)" : "none",
                              color: "#0f172a",
                            }}
                          >
                            <span style={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>{displayName(b, masked)}</span>
                            <span style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{fmtPct(bPct)}</span>
                          </button>
                        );
                      })}
                    </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Branch data-element drill-down — modal so it's always visible no matter
          how far down the 200+ branch grid the clicked tile was. */}
      {openBranch && openItem && (
        <ElementModal
          group={activeGroup}
          item={openItem}
          branch={openBranch}
          liveData={liveData}
          taskGrid={taskGrid}
          extraData={extraData}
          effData={effData}
          attData={attData}
          token={token}
          onClose={() => setOpenBranch(null)}
          onGridChanged={async () => {
            try { const g = await fetchTaskGrid(token); if (g) setTaskGrid(g); } catch (_) {}
          }}
          onAttestationsChanged={(branchName, elementId, att) => {
            setAttData(prev => {
              const next = { ...(prev || {}) };
              next[branchName] = { ...(next[branchName] || {}) };
              if (att) next[branchName][elementId] = att;
              else delete next[branchName][elementId];
              return next;
            });
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Data-element list for one item at one branch. Green = done, red = not done.
// Each row carries the requirement badge + floating help (with optional link).
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// CompliancePrintReport — printable Regulatory Compliance report for ONE
// pharmacy ("Export Regulatory compliance report" on the Single pharmacy
// page). Deliberately NOT a second implementation: it loads its data through
// fetchAllComplianceData and renders every group → item with the SAME
// ElementList component (in printMode, so each element's Review-data panel —
// live drill grids, periodic entry history, source rows — is open), so any
// change to the website's compliance panels flows into the export
// automatically. Printing uses the browser's Print → Save as PDF via
// scoped @media print CSS.
// -----------------------------------------------------------------------------
export function CompliancePrintReport({ pharmacyName, pharmacyDetails = null, token, onClose }) {
  const masked = useMaskNames();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const d = await fetchAllComplianceData(token);
        if (!cancelled) setData(d);
      } catch (e) { if (!cancelled) setError(String(e.message || e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const noop = () => {};
  const liveData = data?.liveData || null;
  const taskGrid = data?.taskGrid || null;
  const extraData = data?.extraData || null;
  const effData = data?.effData || null;
  const attData = data?.attData || null;

  // Branch-level %s from the same helpers the board uses.
  const itemPcts = useMemo(() => {
    if (!data) return {};
    const m = {};
    for (const g of GROUPS) {
      for (const it of (g.items || [])) {
        m[`${g.id}|${it.id}`] = itemBranchPct(g, it, pharmacyName, liveData, taskGrid, extraData, effData, attData);
      }
    }
    return m;
  }, [data, pharmacyName, liveData, taskGrid, extraData, effData, attData]);
  const groupBranchPct = (g) => {
    const vals = (g.items || []).map(it => itemPcts[`${g.id}|${it.id}`]).filter(v => v != null);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  };

  // Optional pharmacy detail lines from whatever the caller knows.
  const detailBits = [];
  if (pharmacyDetails) {
    for (const k of ["address", "addressLine1", "postcode", "postCode", "phone", "telephone", "email", "odsCode"]) {
      const v = pharmacyDetails[k];
      if (v && typeof v !== "object") detailBits.push(String(v));
    }
  }

  return (
    <div className="rc-print-root"
         style={{ position: "fixed", inset: 0, zIndex: 2000, overflowY: "auto",
                  background: "#f1f5f9", padding: "20px 0" }}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .rc-print-root, .rc-print-root * { visibility: visible; }
          .rc-print-root { position: absolute !important; left: 0; top: 0;
                           width: 100%; height: auto !important;
                           overflow: visible !important; background: #fff !important;
                           padding: 0 !important; }
          .rc-noprint { display: none !important; }
          .rc-print-item { break-inside: avoid-page; }
        }
      `}</style>
      <div style={{ maxWidth: 1000, margin: "0 auto", background: "#fff",
                    borderRadius: 10, boxShadow: "0 2px 14px rgba(0,0,0,0.15)",
                    padding: "22px 26px" }}>
        {/* Toolbar (never printed) */}
        <div className="rc-noprint"
             style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
          <button onClick={() => window.print()}
                  disabled={loading || !!error}
                  style={{ fontSize: 13, fontWeight: 700, cursor: "pointer",
                           border: "1px solid #1e293b", background: "#1e293b", color: "#fff",
                           borderRadius: 6, padding: "7px 14px" }}>
            Print / Save as PDF
          </button>
          <button onClick={onClose}
                  style={{ fontSize: 13, fontWeight: 700, cursor: "pointer",
                           border: "1px solid #cbd5e1", background: "#f8fafc", color: "#334155",
                           borderRadius: 6, padding: "7px 14px" }}>
            Close
          </button>
        </div>

        {/* Report header: pharmacy details + date */}
        <div style={{ borderBottom: "3px solid #1e293b", paddingBottom: 12, marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>Regulatory compliance report</h1>
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
            {displayName(pharmacyName, masked)}
          </div>
          {detailBits.length > 0 && (
            <div style={{ fontSize: 12.5, color: "#475569", marginTop: 2 }}>
              {detailBits.join(" · ")}
            </div>
          )}
          <div style={{ fontSize: 12.5, color: "#475569", marginTop: 2 }}>
            {viewerOrg() ? `${viewerOrg()} · ` : ""}
            Generated {new Date().toLocaleString("en-GB")}
          </div>
          <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 4 }}>
            Each data element shows its recorded data source (head office /
            paper / third-party / periodic task) and the supporting evidence —
            the same panels shown on the Regulatory Compliance board.
          </div>
        </div>

        {loading && (
          <div style={{ color: "#92400e", fontSize: 14, padding: "24px 0" }}>
            Loading compliance data for {displayName(pharmacyName, masked)}…
          </div>
        )}
        {error && (
          <div style={{ color: "#991b1b", fontSize: 14, padding: "24px 0" }}>
            Couldn't load compliance data: {error}
          </div>
        )}

        {!loading && !error && data && GROUPS.map((g) => {
          const gPct = groupBranchPct(g);
          return (
            <div key={g.id} style={{ marginBottom: 22 }}>
              {/* Group heading block — same % colouring as the board tabs */}
              <div style={{ background: pctBgStrong(gPct), border: "1px solid #cbd5e1",
                            borderRadius: 8, padding: "10px 14px",
                            display: "flex", justifyContent: "space-between",
                            alignItems: "baseline", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>{g.name}</div>
                  <div style={{ fontSize: 12, color: "#334155", opacity: 0.8 }}>{g.subtitle}</div>
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{fmtPct(gPct)}</div>
              </div>

              {(g.items || []).map((it) => {
                const iPct = itemPcts[`${g.id}|${it.id}`];
                return (
                  <div key={it.id} className="rc-print-item" style={{ marginTop: 10 }}>
                    {/* Item subheading panel */}
                    <div style={{ background: pctBg(iPct), border: "1px solid #e2e8f0",
                                  borderRadius: 8, padding: "6px 12px",
                                  display: "flex", justifyContent: "space-between",
                                  alignItems: "baseline", gap: 10 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>
                        {it.name}
                        {it.subtitle ? <span style={{ fontWeight: 400, color: "#64748b" }}> · {it.subtitle}</span> : null}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{fmtPct(iPct)}</div>
                    </div>
                    {/* Effectiveness metrics above the checklist — same order
                        as the website's branch popup (ElementModal). */}
                    {(it.metrics || []).length > 0 && (
                      <MetricsPanel metrics={it.metrics} branch={pharmacyName}
                                    effData={effData} token={token} />
                    )}
                    {/* The website's own element list, panels open */}
                    <ElementList
                      group={g}
                      item={it}
                      branch={pharmacyName}
                      liveData={liveData}
                      taskGrid={taskGrid}
                      extraData={extraData}
                      attData={attData}
                      token={token}
                      onGridChanged={noop}
                      onAttestationsChanged={noop}
                      printMode
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ElementList({ group, item, branch, liveData, taskGrid, extraData, attData, token, onGridChanged, onAttestationsChanged, printMode = false }) {
  const els = item.elements || [];
  const [openSrc, setOpenSrc] = useState(null); // element id whose source panel is open
  const [entryEl, setEntryEl] = useState(null); // element being updated (task-backed) -> TaskEntryDialog
  const [nmAnalysisOpen, setNmAnalysisOpen] = useState(false); // near-miss tabbed analysis dialog
  const [altEl, setAltEl] = useState(null);     // element being attested (no data stream) -> AltEvidenceDialog

  // Resolve the grid task behind an element (by def id or by name), plus the
  // branch row - everything the entry dialog needs to post a value.
  const taskFor = (elementId) => {
    if (!taskGrid) return null;
    let tId = TASK_DEF_FOR[elementId] || null;
    if (!tId && TASK_DEF_BY_NAME[elementId]) tId = taskGrid.idByName?.[TASK_DEF_BY_NAME[elementId]] || null;
    if (!tId) return null;
    const row = taskGrid.rowsByName?.[branch];
    if (!row || !row.dbName) return null;
    return {
      taskId: tId,
      dataType: taskGrid.dataTypeById?.[tId] || "text",
      options: taskGrid.optionsById?.[tId] || [],
      frequency: taskGrid.frequencyById?.[tId] || null,
      dbName: row.dbName, pharmacyId: row.pharmacyId || null,
      cell: row.cells?.[tId] || null
    };
  };
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, marginTop: 12 }}>
      {/* Sticky column header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, padding: "6px 12px",
        position: "sticky", top: 0, zIndex: 2, background: "#f8fafc",
        borderBottom: "1px solid #e2e8f0", borderTopLeftRadius: 8, borderTopRightRadius: 8,
        fontSize: 10, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.5, color: "#64748b",
      }}>
        <span style={{ width: 18, textAlign: "center" }}>✓</span>
        <span style={{ flex: 1 }}>Data element</span>
        <span style={{ width: 104, textAlign: "center" }}>Data source</span>
        <span style={{ width: 88, textAlign: "center" }}>Review data</span>
        <span style={{ width: 78, textAlign: "center" }}>Requirement</span>
        <span style={{ width: 18, textAlign: "center" }}>ⓘ</span>
      </div>
      <div>
        {els.map((el, i) => {
          const { done, live, noData, alt } = resolveDone(group.id, item.id, branch, el.id, liveData, taskGrid, extraData, attData);
          const src = sourceInfo(el.id, taskGrid);
          // Print/report mode: every element's Review-data panel is open so
          // the exported report carries the same evidence the website shows.
          const isSrcOpen = printMode || openSrc === el.id;
          const task = taskFor(el.id);
          // Alternative-evidence offer: element has no digital stream for this
          // branch (placeholder or wired-with-no-data) and isn't task-backed.
          const canAlt = (!task && (noData || !live || alt)) || ALT_OVERRIDES_LIVE.has(el.id);
          // Four visual states: done (green), alt-evidence (teal-green),
          // not-done (red), no-data (grey).
          const rowBg = alt ? "#ccfbf1" : noData ? "#f8fafc" : done ? "#dcfce7" : "#fee2e2";
          const stateColor = alt ? "#0f766e" : noData ? "#64748b" : done ? "#166534" : "#991b1b";
          const mark = noData && !alt ? "—" : done ? "✓" : "✗";
          // Source-tag label/colour: Live (data present), No data (wired, no row
          // for this branch), or Placeholder (not wired at all).
          const tagLabel = noData ? "No data" : live ? "Live" : "Placeholder";
          const tagBorder = noData ? "#cbd5e1" : live ? "#86efac" : "#e2e8f0";
          const tagBg = noData ? "#e2e8f0" : live ? "#dcfce7" : "#f1f5f9";
          const tagColor = noData ? "#475569" : live ? "#166534" : "#94a3b8";
          return (
            <div key={el.id} style={{ borderTop: i === 0 ? "none" : "1px solid #f1f5f9" }}>
              <div
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 12px",
                  background: rowBg,
                }}
              >
                <span style={{ fontSize: 15, width: 18, textAlign: "center", color: stateColor }}>
                  {mark}
                </span>
                <span style={{ flex: 1, fontSize: 13, color: "#0f172a" }}>
                  {el.label}
                  {task && task.cell && task.cell.note && !alt && (
                    <span style={{ display: "block", fontSize: 11, color: "#64748b",
                                   fontStyle: "italic", marginTop: 1 }}
                          title={task.cell.note}>
                      “{task.cell.note.length > 90 ? task.cell.note.slice(0, 90) + "…" : task.cell.note}”
                    </span>
                  )}
                  {alt && (
                    <span style={{ display: "block", fontSize: 11, color: "#0f766e", marginTop: 1 }}>
                      {alt.source}{alt.recordedAt ? ` · ${new Date(alt.recordedAt).toLocaleDateString("en-GB")}` : ""}
                    </span>
                  )}
                </span>
                {/* DATA SOURCE column — one cell per element, always populated:
                    • periodic-event elements → "Update" button (record a value)
                    • attestable elements     → button showing the recorded
                      source (Head office / Paper / 3rd party…) or "Set source"
                    • live Pharmace-driven elements → static "Head office" tag
                      (the data comes from Pharmace, i.e. the head-office data
                      areas — same thing, so no separate "Pharmace" wording). */}
                <span style={{ width: 104, textAlign: "center", flexShrink: 0 }}>
                  {task ? (
                    <button
                      onClick={() => setEntryEl({ el, task })}
                      title="This item comes from a periodic event — record a new value for it (you can set the date it applies to)"
                      style={{
                          outline: (entryEl && entryEl.el.id === el.id) ? "2px solid #000" : "none", outlineOffset: 1, fontSize: 11, fontWeight: 700, cursor: "pointer",
                               border: "1px solid #93c5fd", background: "#eff6ff", color: "#1d4ed8",
                               borderRadius: 4, padding: "2px 8px", whiteSpace: "nowrap" }}>
                      Update
                    </button>
                  ) : canAlt ? (
                    <button
                      onClick={() => setAltEl(el)}
                      title={alt ? "View / change / remove the data source recorded for this item"
                                 : "Record where the data for this item comes from (head office / paper records / third-party system)"}
                      style={{
                        outline: (altEl && altEl.id === el.id) ? "2px solid #000" : "none", outlineOffset: 1, fontSize: 11, fontWeight: 700, cursor: "pointer",
                               border: alt ? "1px solid #5eead4" : "1px solid #cbd5e1",
                               background: alt ? "#f0fdfa" : "#f8fafc",
                               color: alt ? "#0f766e" : "#475569",
                               borderRadius: 4, padding: "2px 8px", whiteSpace: "nowrap" }}>
                      {alt ? `${shortAltLabel(alt.source)} ✓` : "Set source"}
                    </button>
                  ) : (
                    <span
                      title="This item is driven by live Pharmace data — the evidence is held in the head-office data areas"
                      style={{ display: "inline-block", fontSize: 11, fontWeight: 700,
                               border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569",
                               borderRadius: 4, padding: "2px 8px", whiteSpace: "nowrap" }}>
                      Head office
                    </span>
                  )}
                </span>
                {/* REVIEW DATA column — the ▸ toggle opens the review panel for
                    every element so the underlying data is always inspectable */}
                <span style={{ width: 88, textAlign: "center", flexShrink: 0 }}>
                  <button
                    onClick={() => {
                      // Near-miss + live Pharmace data: the drill IS the full
                      // analysis dialog, not the API source panel.
                      if (el.id === "near-miss" && live && !noData && !src?.placeholder) {
                        setNmAnalysisOpen(true);
                        return;
                      }
                      setOpenSrc(isSrcOpen ? null : el.id);
                    }}
                    title={el.id === "near-miss" && live && !noData && !src?.placeholder
                      ? "Open the full near-miss analysis for this pharmacy (current month, monthly reviews, trends)"
                      : "Show data source / how this value is set"}
                    style={{
                      outline: isSrcOpen ? "2px solid #000" : "none", outlineOffset: 1,
                      fontSize: 10, fontWeight: 700, cursor: "pointer",
                      border: `1px solid ${tagBorder}`,
                      background: tagBg, color: tagColor,
                      borderRadius: 4, padding: "1px 6px", whiteSpace: "nowrap",
                    }}
                  >
                    {isSrcOpen ? "▾ " : "▸ "}{tagLabel}
                  </button>
                </span>
                <span style={{ width: 78, textAlign: "center" }}><ReqBadge requirement={el.requirement} /></span>
                <span style={{ width: 18, textAlign: "center" }}><HelpInfo help={el.help} link={el.link} /></span>
              </div>
              {/* Source / debug panel */}
              {isSrcOpen && src && (
                <div style={{
                  background: "#0f172a", color: "#e2e8f0", padding: "10px 14px",
                  fontSize: 12, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  lineHeight: 1.5,
                  borderLeft: src.placeholder ? "3px solid #f59e0b" : "3px solid #22c55e",
                }}>
                  {src.placeholder && (
                    <div style={{ color: "#fbbf24", fontWeight: 700, marginBottom: 6 }}>
                      ⚠ PLACEHOLDER — not real compliance data
                    </div>
                  )}
                  {noData && !src.placeholder && (
                    <div style={{ color: "#cbd5e1", fontWeight: 700, marginBottom: 6 }}>
                      ℹ Wired to a live source, but this branch has no data — the
                      endpoint returned no row for it (module not active / no
                      records for this pharmacy). Shown as “not done”.
                    </div>
                  )}
                  {/* Cleaning + live Pharmace data: skip the API/source rows —
                      show the per-area breakdown (on-time % + current state)
                      and the individual cleaning events instead. */}
                  {el.id === "env-clean" && live && !noData && !src.placeholder ? (
                    <CleaningAreasGrid
                      pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null}
                      token={token}
                    />
                  ) : el.id === "tos-hours"
                      && ((alt && /^head office/i.test(alt.source || ""))
                          || (!alt && live && !noData && !src.placeholder)) ? (
                    /* Core & supplementary hours: workforce grid — staff count
                       per role, hours worked last month (timesheet attendance
                       data), latest item count from Periodic Tasks, and hours
                       per 1000 items. Shown when "Head office holds evidence"
                       is the recorded source, or by default while the element
                       is on the live stream with no source recorded. Paper /
                       third-party / other sources fall through to the plain
                       source rows. */
                    <WorkforceHoursGrid
                      pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null}
                      token={token}
                    />
                  ) : (el.id === "riddor-book" || el.id === "riddor-report")
                      && alt && /^head office/i.test(alt.source || "") ? (
                    /* Accident book / RIDDOR + "Head office holds evidence":
                       list this branch's accidents — the RIDDOR element shows
                       only entries ticked reportable, with the HSE reference. */
                    <AccidentListGrid
                      pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null}
                      token={token}
                      riddorOnly={el.id === "riddor-report"}
                    />
                  ) : el.id === "sup-mhra" && alt && /^head office/i.test(alt.source || "") ? (
                    /* MHRA + "Head office holds evidence": response
                       percentages for this branch as coloured bars. */
                    <MhraStatusBars
                      pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null}
                      token={token}
                    />
                  ) : el.id === "adv-nms" && alt && /^head office/i.test(alt.source || "") ? (
                    /* NMS consent + "Head office holds evidence": bar showing
                       the % of NMS records with patient consent recorded. */
                    <NmsConsentBar
                      pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null}
                      token={token}
                    />
                  ) : el.id === "lfpse" && alt && /^head office/i.test(alt.source || "") ? (
                    /* Patient safety + "Head office holds evidence": show the
                       incident dates recorded in Pharmace as the supporting
                       evidence grid. */
                    <PatientIncidentsGrid
                      pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null}
                      token={token}
                    />
                  ) : el.id === "rp-log" && live && !noData && !src.placeholder ? (
                    /* RP record + live Pharmace data: signed-in-now, % days
                       covered, and the individual sign-in sessions. */
                    <RpStatusGrid
                      pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null}
                      token={token}
                    />
                  ) : task ? (
                    /* Periodic-event element: Review data shows the recorded
                       entries for this item, newest first. */
                    <TaskEntryHistoryGrid
                      label={el.label}
                      taskId={task.taskId}
                      dbName={task.dbName}
                      dataType={task.dataType}
                      token={token}
                      refreshKey={task.cell?.entryId || task.cell?.recordedAt || ""}
                    />
                  ) : (
                    <>
                      <SrcRow k="Source" v={src.kind} />
                      <SrcRow k="Call" v={`${src.method} ${src.path}`} mono />
                      <SrcRow k="Params" v={src.params} />
                      <SrcRow k="Branch match" v={src.field} />
                      <SrcRow k="Rule" v={src.rule} highlight />
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {nmAnalysisOpen && (
        <NearMissPharmacyDialog
          pharmacyName={branch}
          pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null}
          onClose={() => setNmAnalysisOpen(false)}
        />
      )}

      {entryEl && (
        <PeriodicTaskEntryDialog
          token={token}
          task={{ taskId: entryEl.task.taskId,
                  description: entryEl.el.label,
                  dataType: entryEl.task.dataType,
                  frequency: entryEl.task.frequency,
                  options: entryEl.task.options || [] }}
          target={{ dbName: entryEl.task.dbName,
                    pharmacyId: entryEl.task.pharmacyId,
                    pharmacyName: branch }}
          title={`Update: ${entryEl.el.label}`}
          existingEntry={entryEl.task.cell?.entryId ? {
            entryId: entryEl.task.cell.entryId, value: entryEl.task.cell.value,
            recordedAt: entryEl.task.cell.recordedAt, note: entryEl.task.cell.note,
            recordedBy: entryEl.task.cell.recordedBy } : null}
          onClose={() => setEntryEl(null)}
          onSaved={() => { setEntryEl(null); onGridChanged && onGridChanged(); }}
        />
      )}
      {altEl && (
        <AltEvidenceDialog
          el={altEl} branch={branch} token={token}
          existing={attData && attData[branch] ? attData[branch][altEl.id] : null}
          dbName={taskGrid?.rowsByName?.[branch]?.dbName || null}
          pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null}
          onClose={() => setAltEl(null)}
          onChanged={(att) => { setAltEl(null); onAttestationsChanged && onAttestationsChanged(branch, altEl.id, att); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// CleaningAreasGrid — per-area cleaning lines inside the "Live" source panel
// of the "Cleaning log up to date" element. For each active area: on-time %
// over the last 12 months, last cleaned / next due, and whether it is
// currently up to date and clean (same concepts as the date-checking view).
// -----------------------------------------------------------------------------
function CleaningAreasGrid({ pharmacyId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) { setLoading(false); return; }
    setLoading(true); setErr("");
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/headoffice/${withOrgScope(`compliance/cleaning-areas?pharmacyId=${pharmacyId}`)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) { if (!cancelled) setErr(String(e.message || e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [pharmacyId, token]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-GB") : "—";
  const pctColor = (p) => p == null ? "#94a3b8" : p >= 90 ? "#4ade80" : p >= 60 ? "#fbbf24" : "#f87171";

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
        Areas — on-time % (12 mo) & current state
      </div>
      {!pharmacyId ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Area breakdown unavailable for this branch.</div>
      ) : loading ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading areas…</div>
      ) : err ? (
        <div style={{ color: "#fca5a5", fontSize: 12 }}>Couldn't load areas: {err}</div>
      ) : !(data?.areas || []).length ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>No active cleaning areas at this pharmacy.</div>
      ) : (
        <div style={{ overflowY: "auto", maxHeight: 260 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 11, color: "#e2e8f0", width: "100%" }}>
            <thead>
              <tr>
                {["Area", "Frequency", "Last cleaned", "Next due", "On time (12 mo)", "Current state"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "4px 8px", position: "sticky", top: 0,
                                       background: "#0f172a", color: "#94a3b8", fontWeight: 700,
                                       textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.areas.map((a, i) => (
                <tr key={i} style={{ borderTop: "1px solid rgba(148,163,184,0.2)" }}>
                  <td style={{ padding: "4px 8px" }}>{a.areaName}</td>
                  <td style={{ padding: "4px 8px", color: "#94a3b8", textTransform: "capitalize" }}>{a.frequency || "—"}</td>
                  <td style={{ padding: "4px 8px" }}>{fmt(a.lastCleaned)}</td>
                  <td style={{ padding: "4px 8px" }}>{fmt(a.nextDue)}</td>
                  <td style={{ padding: "4px 8px", fontWeight: 800, color: pctColor(a.onTimePct) }}
                      title={a.cleans ? `${a.cleans} clean${a.cleans === 1 ? "" : "s"} recorded in the last 12 months` : "No cleans recorded in the last 12 months"}>
                    {a.onTimePct == null ? "—" : `${a.onTimePct}%`}
                  </td>
                  <td style={{ padding: "4px 8px", fontWeight: 700,
                               color: a.upToDate ? "#4ade80" : a.neverLogged ? "#94a3b8" : "#f87171" }}>
                    {a.upToDate ? "Up to date & clean" : a.neverLogged ? "Never logged" : "Overdue"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Individual cleaning events beneath the status grid */}
      {(data?.events || []).length > 0 && (
        <>
          <div style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: 0.4,
                        margin: "12px 0 6px", borderTop: "1px solid #1e293b", paddingTop: 8 }}>
            Recent cleaning events ({data.events.length}{data.events.length === 50 ? ", latest" : ""})
          </div>
          <div style={{ overflowY: "auto", maxHeight: 220 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 11, color: "#e2e8f0", width: "100%" }}>
              <thead>
                <tr>
                  {["Date", "Area", "Cleaned by", "Notes"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "4px 8px", position: "sticky", top: 0,
                                         background: "#0f172a", color: "#94a3b8", fontWeight: 700,
                                         textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.events.map((e, i) => (
                  <tr key={i} style={{ borderTop: "1px solid rgba(148,163,184,0.2)" }}>
                    <td style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>{fmt(e.date)}</td>
                    <td style={{ padding: "4px 8px" }}>{e.areaName}</td>
                    <td style={{ padding: "4px 8px" }}>{e.by || "—"}</td>
                    <td style={{ padding: "4px 8px", color: "#94a3b8" }}>{e.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// WorkforceHoursGrid — workforce detail inside the "Live" source panel of the
// "Core & supplementary hours met" element. Per role: active staff count and
// hours worked over the last complete calendar month (timesheet attendance
// data), plus the branch's latest item count from Periodic Tasks and the
// derived hours per 1000 items. Unknown figures render as n/k.
// -----------------------------------------------------------------------------
function WorkforceHoursGrid({ pharmacyId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) { setLoading(false); return; }
    setLoading(true); setErr("");
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/headoffice/${withOrgScope(`compliance/workforce-hours?pharmacyId=${pharmacyId}`)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) { if (!cancelled) setErr(String(e.message || e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [pharmacyId, token]);

  const NK = <span style={{ color: "#94a3b8" }}>n/k</span>;
  const items = data?.itemCount ?? null;
  const per1000 = (hours) =>
    (hours != null && items != null && items > 0)
      ? (Math.round((hours / (items / 1000)) * 10) / 10).toFixed(1)
      : null;

  const roles = data?.roles || [];
  const totals = data?.totals || null;

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
        Workforce — roles, hours worked{data?.month ? ` (${data.month})` : ""} &
        items dispensed
      </div>
      {!pharmacyId ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Workforce breakdown unavailable for this branch.</div>
      ) : loading ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading workforce…</div>
      ) : err ? (
        <div style={{ color: "#fca5a5", fontSize: 12 }}>Couldn't load workforce: {err}</div>
      ) : !roles.length ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>No active staff recorded for this pharmacy.</div>
      ) : (
        <>
          <div style={{ overflowY: "auto", maxHeight: 260 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 11, color: "#e2e8f0", width: "100%" }}>
              <thead>
                <tr>
                  {["Role", "Staff", `Hours / month`, "Items / month", "Hours per 1000 items"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "4px 8px", position: "sticky", top: 0,
                                         background: "#0f172a", color: "#94a3b8", fontWeight: 700,
                                         textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roles.map((r, i) => (
                  <tr key={i} style={{ borderTop: "1px solid rgba(148,163,184,0.2)" }}>
                    <td style={{ padding: "4px 8px" }}>{r.role}</td>
                    <td style={{ padding: "4px 8px" }}>{r.staffCount}</td>
                    <td style={{ padding: "4px 8px" }}
                        title="Hours worked in the last complete calendar month, from clock-in/out attendance minus breaks taken">
                      {r.hoursMonth != null ? r.hoursMonth.toFixed(1) : NK}
                    </td>
                    <td style={{ padding: "4px 8px" }}
                        title={data?.itemTaskDescription
                          ? `Latest “${data.itemTaskDescription}” value from Periodic Tasks`
                          : "No item-count entry found in Periodic Tasks"}>
                      {items != null ? items.toLocaleString() : NK}
                    </td>
                    <td style={{ padding: "4px 8px", fontWeight: 700 }}>
                      {per1000(r.hoursMonth) ?? NK}
                    </td>
                  </tr>
                ))}
                {totals && (
                  <tr style={{ borderTop: "2px solid rgba(148,163,184,0.45)", fontWeight: 800 }}>
                    <td style={{ padding: "4px 8px" }}>Total</td>
                    <td style={{ padding: "4px 8px" }}>{totals.staffCount}</td>
                    <td style={{ padding: "4px 8px" }}>
                      {totals.hoursMonth != null ? totals.hoursMonth.toFixed(1) : NK}
                    </td>
                    <td style={{ padding: "4px 8px" }}>
                      {items != null ? items.toLocaleString() : NK}
                    </td>
                    <td style={{ padding: "4px 8px" }}>
                      {per1000(totals.hoursMonth) ?? NK}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ color: "#64748b", fontSize: 10.5, marginTop: 6 }}>
            Hours = clock-in/out attendance minus breaks (timesheet data);
            items = latest Periodic Tasks entry
            {data?.itemRecordedAt
              ? ` (recorded ${new Date(data.itemRecordedAt).toLocaleDateString("en-GB")})`
              : ""}. n/k = not known.
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// RpStatusGrid — Responsible Pharmacist detail inside the "Live" source panel
// of the RP elements: whether an RP is signed in right now (and who / since
// when), the % of the last 30 days with any RP cover, and the individual
// sign-in sessions.
// -----------------------------------------------------------------------------
function RpStatusGrid({ pharmacyId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) { setLoading(false); return; }
    setLoading(true); setErr("");
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/headoffice/${withOrgScope(`compliance/rp-status?pharmacyId=${pharmacyId}`)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) { if (!cancelled) setErr(String(e.message || e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [pharmacyId, token]);

  const fmtDT = (d) => d ? new Date(d).toLocaleString("en-GB",
    { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const pct = data?.pctDaysCovered;
  const pctColor = pct == null ? "#94a3b8" : pct >= 90 ? "#4ade80" : pct >= 60 ? "#fbbf24" : "#f87171";

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {!pharmacyId ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>RP detail unavailable for this branch.</div>
      ) : loading ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading RP record…</div>
      ) : err ? (
        <div style={{ color: "#fca5a5", fontSize: 12 }}>Couldn't load RP record: {err}</div>
      ) : (
        <>
          {/* Headline stats: signed in now + % days covered */}
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 10 }}>
            <div>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700,
                            textTransform: "uppercase", letterSpacing: 0.4 }}>RP signed in now</div>
              <div style={{ fontSize: 14, fontWeight: 800,
                            color: data.currentlySignedIn ? "#4ade80" : "#f87171" }}>
                {data.currentlySignedIn ? "Yes" : "No"}
                {data.currentlySignedIn && data.currentRp && (
                  <span style={{ fontWeight: 400, fontSize: 12, color: "#e2e8f0" }}>
                    {" "}— {data.currentRp.name || "RP"}{data.currentRp.since ? ` since ${fmtDT(data.currentRp.since)}` : ""}
                  </span>
                )}
              </div>
            </div>
            <div>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700,
                            textTransform: "uppercase", letterSpacing: 0.4 }}>
                {data.tradingDaysOnly ? "Trading days covered" : "Days covered"} (last {data.windowDays || 30} days)
              </div>
              <div style={{ fontSize: 14, fontWeight: 800, color: pctColor }}>
                {pct == null ? "—" : `${pct}%`}
                <span style={{ fontWeight: 400, fontSize: 12, color: "#94a3b8" }}>
                  {" "}({data.daysCovered}/{data.tradingDays ?? data.windowDays} {data.tradingDaysOnly ? "trading days" : "days"} with RP cover)
                </span>
              </div>
            </div>
          </div>

          {/* Individual sign-in sessions */}
          <div style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: 0.4,
                        margin: "0 0 6px", borderTop: "1px solid #1e293b", paddingTop: 8 }}>
            Sign-in sessions ({(data.sessions || []).length}{(data.sessions || []).length === 30 ? ", latest" : ""})
          </div>
          {!(data.sessions || []).length ? (
            <div style={{ color: "#94a3b8", fontSize: 12 }}>No RP sessions recorded.</div>
          ) : (
            <div style={{ overflowY: "auto", maxHeight: 240 }}>
              <table style={{ borderCollapse: "collapse", fontSize: 11, color: "#e2e8f0", width: "100%" }}>
                <thead>
                  <tr>
                    {["Pharmacist", "Signed in", "Signed out", "CD keys"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "4px 8px", position: "sticky", top: 0,
                                           background: "#0f172a", color: "#94a3b8", fontWeight: 700,
                                           textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.sessions.map((x, i) => (
                    <tr key={i} style={{ borderTop: "1px solid rgba(148,163,184,0.2)",
                                         background: x.open ? "rgba(251,191,36,0.12)" : "transparent" }}>
                      <td style={{ padding: "4px 8px" }}>{x.pharmacistName || "—"}</td>
                      <td style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>{fmtDT(x.signInTime)}</td>
                      <td style={{ padding: "4px 8px", whiteSpace: "nowrap",
                                   color: x.open ? "#fbbf24" : "#e2e8f0", fontWeight: x.open ? 700 : 400 }}>
                        {x.open ? "Still open" : fmtDT(x.signOutTime)}
                      </td>
                      <td style={{ padding: "4px 8px", color: "#94a3b8" }}>
                        {x.cdKeysReturnedToSafe == null ? "—" : (x.cdKeysReturnedToSafe ? "Returned to safe" : "Handed over")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// PatientIncidentsGrid — patient-safety incident dates inside the source panel
// of the LFPSE element when "Head office holds evidence" is the recorded
// source: the incidents logged in Pharmace, newest first, as the supporting
// evidence grid.
// -----------------------------------------------------------------------------
function PatientIncidentsGrid({ pharmacyId, token }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) { setLoading(false); return; }
    setLoading(true); setErr("");
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/headoffice/single/patientSafety?pharmacyId=${pharmacyId}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setRows(json?.incidents || []);
      } catch (e) { if (!cancelled) setErr(String(e.message || e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [pharmacyId, token]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-GB") : "—";

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
        Patient safety incidents recorded ({(rows || []).length})
      </div>
      {!pharmacyId ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Incident record unavailable for this branch.</div>
      ) : loading ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading incidents…</div>
      ) : err ? (
        <div style={{ color: "#fca5a5", fontSize: 12 }}>Couldn't load incidents: {err}</div>
      ) : !(rows || []).length ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>No patient safety incidents recorded in Pharmace for this branch.</div>
      ) : (
        <div style={{ overflowY: "auto", maxHeight: 260 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 11, color: "#e2e8f0", width: "100%" }}>
            <thead>
              <tr>
                {["Incident date", "Harm level", "LFPSE", "LFPSE ref", "RP notified"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "4px 8px", position: "sticky", top: 0,
                                       background: "#0f172a", color: "#94a3b8", fontWeight: 700,
                                       textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.incidentId || i} style={{ borderTop: "1px solid rgba(148,163,184,0.2)",
                                                     background: r.lfpseIncomplete ? "rgba(248,113,113,0.12)" : "transparent" }}>
                  <td style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>{fmt(r.incidentDate)}</td>
                  <td style={{ padding: "4px 8px", color: "#94a3b8" }}>{r.harmLevelId ?? "—"}</td>
                  <td style={{ padding: "4px 8px" }}>
                    {r.submitLfpse
                      ? <span style={{ color: r.lfpseIncomplete ? "#f87171" : "#4ade80", fontWeight: 700 }}>
                          {r.lfpseIncomplete ? "Ticked — no ref" : "Submitted"}
                        </span>
                      : <span style={{ color: "#94a3b8" }}>Not required</span>}
                  </td>
                  <td style={{ padding: "4px 8px" }}>{r.lfpseRef || "—"}</td>
                  <td style={{ padding: "4px 8px", color: r.rpNotified ? "#4ade80" : "#94a3b8" }}>
                    {r.rpNotified ? "Yes" : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// MhraStatusBars — MHRA response percentages inside the source panel of the
// "MHRA drug/safety alerts actioned" element when "Head office holds
// evidence" is the recorded source: % acknowledged, % relevant, % actioned,
// each as a coloured bar with the underlying counts.
// -----------------------------------------------------------------------------
function MhraStatusBars({ pharmacyId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) { setLoading(false); return; }
    setLoading(true); setErr("");
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/headoffice/compliance/mhra-status?pharmacyId=${pharmacyId}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) { if (!cancelled) setErr(String(e.message || e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [pharmacyId, token]);

  const barColor = (p) => p == null ? "#475569" : p >= 90 ? "#4ade80" : p >= 60 ? "#fbbf24" : "#f87171";
  const Bar = ({ label, pct, detail }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
        <span style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700,
                       textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: barColor(pct) }}>
          {pct == null ? "—" : `${pct}%`}
          <span style={{ fontWeight: 400, fontSize: 11, color: "#94a3b8", marginLeft: 6 }}>{detail}</span>
        </span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: "rgba(148,163,184,0.25)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.max(0, Math.min(100, pct || 0))}%`,
                      background: barColor(pct), borderRadius: 4 }} />
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
        MHRA alert response — this branch
      </div>
      {!pharmacyId ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>MHRA detail unavailable for this branch.</div>
      ) : loading ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading MHRA figures…</div>
      ) : err ? (
        <div style={{ color: "#fca5a5", fontSize: 12 }}>Couldn't load MHRA figures: {err}</div>
      ) : !data || !data.total ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>No live MHRA alerts to respond to.</div>
      ) : (
        <>
          <Bar label="Acknowledged" pct={data.pctAcknowledged}
               detail={`${data.acknowledged}/${data.total} alerts with a recorded response`} />
          <Bar label="Relevant to this branch" pct={data.pctRelevant}
               detail={`${data.relevant}/${data.total} alerts marked as affecting stock`} />
          <Bar label="Actioned" pct={data.pctActioned}
               detail={data.relevant
                 ? `${data.actionedOfRelevant}/${data.relevant} relevant alerts with action recorded`
                 : "no relevant alerts"} />
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// NmsConsentBar — inside the source panel of the "NMS consent & records
// compliant" element when "Head office holds evidence" is the recorded
// source: a single coloured bar showing the percentage of this branch's NMS
// records with patient consent recorded. n/k when the consent flag can't be
// established from the data.
// -----------------------------------------------------------------------------
function NmsConsentBar({ pharmacyId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) { setLoading(false); return; }
    setLoading(true); setErr("");
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/headoffice/${withOrgScope(`compliance/nms-consent?pharmacyId=${pharmacyId}`)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) { if (!cancelled) setErr(String(e.message || e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [pharmacyId, token]);

  const pct = data?.pct ?? null;
  const barColor = (p) => p == null ? "#475569" : p >= 90 ? "#4ade80" : p >= 60 ? "#fbbf24" : "#f87171";

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
        NMS consent — this branch
      </div>
      {!pharmacyId ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>NMS detail unavailable for this branch.</div>
      ) : loading ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading NMS figures…</div>
      ) : err ? (
        <div style={{ color: "#fca5a5", fontSize: 12 }}>Couldn't load NMS figures: {err}</div>
      ) : !data || !data.total ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>No NMS records for this pharmacy.</div>
      ) : pct == null ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>
          {data.total.toLocaleString()} NMS record{data.total === 1 ? "" : "s"} found,
          but consent status: <span style={{ fontWeight: 700 }}>n/k</span> — no
          consent flag recognised in the NMS data.
        </div>
      ) : (
        <div style={{ marginBottom: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
            <span style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700,
                           textTransform: "uppercase", letterSpacing: 0.4 }}>Consent obtained</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: barColor(pct) }}>
              {pct}%
              <span style={{ fontWeight: 400, fontSize: 11, color: "#94a3b8", marginLeft: 6 }}>
                {data.consented}/{data.total} NMS records with consent recorded
              </span>
            </span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: "rgba(148,163,184,0.25)", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.max(0, Math.min(100, pct))}%`,
                          background: barColor(pct), borderRadius: 4 }} />
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// AccidentListGrid — accident-book entries inside the source panel of the
// Accidents & RIDDOR elements when "Head office holds evidence" is the
// recorded source. riddorOnly=false: every accident. riddorOnly=true: only
// entries ticked RIDDOR-reportable, with the HSE reference number.
// -----------------------------------------------------------------------------
function AccidentListGrid({ pharmacyId, token, riddorOnly = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) { setLoading(false); return; }
    setLoading(true); setErr("");
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/headoffice/single/accidentBook?pharmacyId=${pharmacyId}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) { if (!cancelled) setErr(String(e.message || e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [pharmacyId, token]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-GB") : "—";
  const STATUS = {
    NOT_YET_KNOWN: ["#fbbf24", "Decision pending"],
    REPORTABLE: ["#f87171", "RIDDOR reportable"],
    NOT_REPORTABLE: ["#94a3b8", "Not reportable"],
  };

  // Flatten the three buckets into one dated list; RIDDOR view keeps only
  // the reportable entries.
  const rows = data
    ? (riddorOnly
        ? (data.reportable || [])
        : [...(data.notYetKnown || []), ...(data.reportable || []), ...(data.notReportable || [])])
        .slice()
        .sort((a, b) => new Date(b.accidentDate || 0) - new Date(a.accidentDate || 0))
    : [];

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
        {riddorOnly ? `RIDDOR-reportable accidents (${rows.length})` : `Accident book entries (${rows.length})`}
      </div>
      {!pharmacyId ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Accident record unavailable for this branch.</div>
      ) : loading ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading accidents…</div>
      ) : err ? (
        <div style={{ color: "#fca5a5", fontSize: 12 }}>Couldn't load accidents: {err}</div>
      ) : rows.length === 0 ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>
          {riddorOnly ? "No accidents ticked as RIDDOR reportable." : "No accidents recorded at this branch."}
        </div>
      ) : (
        <div style={{ overflowY: "auto", maxHeight: 260 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 11, color: "#e2e8f0", width: "100%" }}>
            <thead>
              <tr>
                {(riddorOnly
                  ? ["Date", "Person", "Detail", "Reported to HSE", "RIDDOR reference"]
                  : ["Date", "Person", "Location", "Detail", "RIDDOR status"]).map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "4px 8px", position: "sticky", top: 0,
                                       background: "#0f172a", color: "#94a3b8", fontWeight: 700,
                                       textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const st = STATUS[r.riddorStatus] || STATUS.NOT_REPORTABLE;
                const detail = r.accidentDetail
                  ? (r.accidentDetail.length > 70 ? r.accidentDetail.slice(0, 70) + "…" : r.accidentDetail)
                  : "—";
                return (
                  <tr key={r.entryId || i}
                      title={r.accidentDetail || undefined}
                      style={{ borderTop: "1px solid rgba(148,163,184,0.2)" }}>
                    <td style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>{fmt(r.accidentDate)}</td>
                    <td style={{ padding: "4px 8px" }}>{r.patientName || r.reportedByName || "—"}</td>
                    {riddorOnly ? (
                      <>
                        <td style={{ padding: "4px 8px" }}>{detail}</td>
                        <td style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>{fmt(r.riddorReportedAt)}</td>
                        <td style={{ padding: "4px 8px", fontWeight: 700,
                                     color: r.riddorReferenceNumber ? "#4ade80" : "#f87171" }}>
                          {r.riddorReferenceNumber || "No reference recorded"}
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: "4px 8px" }}>{r.accidentLocation || "—"}</td>
                        <td style={{ padding: "4px 8px" }}>{detail}</td>
                        <td style={{ padding: "4px 8px", fontWeight: 700, color: st[0] }}>{st[1]}</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// TaskEntryHistoryGrid — the "Review data" panel for periodic-event elements.
// Fetches every recorded entry for (task × branch) from
// GET /api/headoffice/periodic-tasks/history and lists them newest-first
// (the endpoint already sorts recordedAt desc; re-sorted defensively here).
// refreshKey changes when a new value is saved via Update, re-fetching.
// -----------------------------------------------------------------------------
function TaskEntryHistoryGrid({ label, taskId, dbName, dataType, token, refreshKey }) {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!taskId || !dbName) { setLoading(false); return; }
    setLoading(true); setErr("");
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/headoffice/periodic-tasks/history?taskId=${encodeURIComponent(taskId)}&dbName=${encodeURIComponent(dbName)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const list = (json?.entries || []).slice().sort((a, b) =>
          new Date(b.recordedAt || 0) - new Date(a.recordedAt || 0));
        if (!cancelled) setEntries(list);
      } catch (e) { if (!cancelled) setErr(String(e.message || e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [taskId, dbName, token, refreshKey]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB") : "—";
  const fmtVal = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (dataType === "boolean") return (v === true || v === "true" || v === "yes") ? "Yes" : "No";
    if (dataType === "date") return fmtDate(v);
    return String(v);
  };

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
        Recorded entries — {label} ({(entries || []).length})
      </div>
      {loading ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading entries…</div>
      ) : err ? (
        <div style={{ color: "#fca5a5", fontSize: 12 }}>Couldn't load entries: {err}</div>
      ) : !(entries || []).length ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>
          No entries recorded yet for this item at this branch — use the Update
          button to record the first one.
        </div>
      ) : (
        <div style={{ overflowY: "auto", maxHeight: 260 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 11, color: "#e2e8f0", width: "100%" }}>
            <thead>
              <tr>
                {["Date", "Value", "Recorded by", "Note"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "4px 8px", position: "sticky", top: 0,
                                       background: "#0f172a", color: "#94a3b8", fontWeight: 700,
                                       textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={e._id || i} style={{ borderTop: "1px solid rgba(148,163,184,0.2)" }}>
                  <td style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>
                    {e.periodKey ? e.periodKey : fmtDate(e.recordedAt)}
                  </td>
                  <td style={{ padding: "4px 8px", fontWeight: 700 }}>{fmtVal(e.value)}</td>
                  <td style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>{e.recordedBy || "—"}</td>
                  <td style={{ padding: "4px 8px", color: "#cbd5e1" }}>
                    {e.note ? (e.note.length > 120 ? e.note.slice(0, 120) + "…" : e.note) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// AltEvidenceDialog: record (or remove) an "alternative evidence of compliance"
// attestation for an element with no digital data stream at this branch -
// e.g. the record is kept on paper. Stored per (pharmacy, element) and shown
// as a teal "Alt evidence" state on the board.
// -----------------------------------------------------------------------------
function AltEvidenceDialog({ el, branch, token, existing, dbName, pharmacyId, onClose, onChanged }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  // One consistent option list for every element. "Head office holds
  // evidence" = the data is in Pharmace (head-office data areas), so no
  // separate "Pharmace (live data)" option is offered.
  const sourceOptions = ALT_SOURCES;
  const [source, setSource] = useState(
    existing?.source && ALT_SOURCES.includes(existing.source) ? existing.source
      : existing?.source ? "Other"
      : ALT_SOURCES[0]);
  const [otherText, setOtherText] = useState(existing?.source && !ALT_SOURCES.includes(existing.source) ? existing.source : "");
  const [note, setNote] = useState(existing?.note || "");
  const [dateStr, setDateStr] = useState(existing?.recordedAt ? new Date(existing.recordedAt).toISOString().slice(0, 10) : todayStr);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const effSource = source === "Other" ? otherText.trim() : source;

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = {
        dbName, pharmacyName: branch, elementId: el.id,
        source: effSource, note: note.trim(),
        recordedAt: new Date(`${dateStr}T12:00:00`).toISOString(),
      };
      const d = await postAttestation(token, "set", body);
      onChanged(d.attestation || { ...body });
    } catch (e) { setErr(String(e.message || e)); setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove the recorded data source for this item? The element will show as having no data again.")) return;
    setBusy(true); setErr("");
    try {
      await postAttestation(token, "clear", { dbName, elementId: el.id });
      onChanged(null);
    } catch (e) { setErr(String(e.message || e)); setBusy(false); }
  };

  const inputStyle = { padding: "7px 10px", fontSize: 13, border: "1px solid #cbd5e1",
                       borderRadius: 6, width: "100%", boxSizing: "border-box", outline: "none" };
  const noDb = !dbName;
  return (
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200 }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ width: "100%", maxWidth: 430, background: "#fff", borderRadius: 10,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.3)", padding: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", marginBottom: 2 }}>
          Data source: {el.label}
        </div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>{branch}</div>
        <div style={{ fontSize: 12, color: "#475569", background: "#f0fdfa", border: "1px solid #99f6e4",
                      borderRadius: 6, padding: "8px 10px", marginBottom: 12, lineHeight: 1.45 }}>
          Record where the data for this item comes from. “Head office holds
          evidence” means it is held in Pharmace (the head-office data areas);
          otherwise choose paper records or a third-party system. The recorded
          source is shown against the element and it counts as compliant until
          the record is removed.
        </div>
        {noDb && (
          <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 10 }}>
            This branch isn’t in the periodic-task grid, so its database
            reference can’t be resolved — attestation can’t be saved.
          </div>
        )}

        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#475569",
                        textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Data source</label>
        <select value={source} onChange={(e) => setSource(e.target.value)} style={inputStyle}>
          {sourceOptions.map((sName) => <option key={sName} value={sName}>{sName}</option>)}
        </select>
        {source === "Other" && (
          <input type="text" value={otherText} placeholder="Describe the source…" maxLength={80}
                 onChange={(e) => setOtherText(e.target.value)}
                 style={{ ...inputStyle, marginTop: 6 }} />
        )}

        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#475569",
                        textTransform: "uppercase", letterSpacing: 0.5, margin: "12px 0 4px" }}>
          Note (optional)
        </label>
        <input type="text" value={note} maxLength={500}
               placeholder="e.g. Signed indemnity certificate in the SOP folder"
               onChange={(e) => setNote(e.target.value)} style={inputStyle} />

        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#475569",
                        textTransform: "uppercase", letterSpacing: 0.5, margin: "12px 0 4px" }}>
          Date verified
        </label>
        <input type="date" value={dateStr} max={todayStr}
               onChange={(e) => setDateStr(e.target.value)} style={inputStyle} />

        {err && <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 10 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "space-between", marginTop: 16 }}>
          <span>
            {existing && (
              <button onClick={remove} disabled={busy}
                      style={{ padding: "7px 14px", fontSize: 13, border: "1px solid #fca5a5",
                               background: "#fef2f2", color: "#b91c1c", borderRadius: 6, cursor: "pointer" }}>Remove</button>
            )}
          </span>
          <span style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} disabled={busy}
                    style={{ padding: "7px 14px", fontSize: 13, border: "1px solid #cbd5e1",
                             background: "#fff", color: "#475569", borderRadius: 6, cursor: "pointer" }}>Cancel</button>
            <button onClick={save} disabled={busy || !effSource || !dateStr || noDb}
                    style={{ padding: "7px 14px", fontSize: 13, border: "none", fontWeight: 700,
                             background: busy ? "#5eead4" : "#0f766e", color: "#fff", borderRadius: 6, cursor: "pointer" }}>
              {busy ? "Saving…" : existing ? "Update" : "Save"}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

// One labelled line in the source/debug panel.
function SrcRow({ k, v, mono, highlight }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 3 }}>
      <span style={{ color: "#64748b", minWidth: 92, flexShrink: 0 }}>{k}:</span>
      <span style={{
        color: highlight ? "#86efac" : "#e2e8f0",
        fontFamily: mono || highlight ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit",
        wordBreak: "break-word",
      }}>{v}</span>
    </div>
  );
}

// Modal overlay wrapping the data-element list. Used for the branch drill-down
// so it's always visible regardless of how far down the branch grid the user
// clicked. Closes on backdrop click or the × button.
function ElementModal({ group, item, branch, liveData, taskGrid, extraData, effData, attData, token, onClose, onGridChanged, onAttestationsChanged }) {
  const masked = useMaskNames();
  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const metrics = item.metrics || [];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "3vh 16px 3vh", zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 660, maxHeight: "92vh", minHeight: "40vh",
          display: "flex", flexDirection: "column",
          background: "#fff", borderRadius: 12, boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        {/* Fixed modal header (does not scroll) */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 16px", borderBottom: "1px solid #e2e8f0",
          background: "#fff", flexShrink: 0,
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>
            {displayName(branch, masked)}
            <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: 13 }}> · {item.name}</span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              border: "none", background: "#f1f5f9", color: "#334155", cursor: "pointer",
              width: 30, height: 30, borderRadius: 8, fontSize: 18, lineHeight: 1, fontWeight: 700,
            }}
          >
            ×
          </button>
        </div>
        {/* Scrollable body. Effectiveness metrics (12-month % bars) sit above the
            point-in-time element checklist. */}
        <div style={{ overflowY: "auto", flex: 1, padding: "0 12px 12px" }}>
          {metrics.length > 0 && (
            <MetricsPanel metrics={metrics} branch={branch} effData={effData} token={token} />
          )}
          <ElementList
            group={group}
            item={item}
            branch={branch}
            liveData={liveData}
            taskGrid={taskGrid}
            extraData={extraData}
            attData={attData}
            token={token}
            onGridChanged={onGridChanged}
            onAttestationsChanged={onAttestationsChanged}
          />
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// MetricsPanel — the 12-month effectiveness view for a process item (fridge,
// cleaning…). Each metric is a coloured % bar vs its threshold, expandable to
// the four-tier maturity drill-down (ever used → recently → current → year %).
// -----------------------------------------------------------------------------
function MetricsPanel({ metrics, branch, effData, token }) {
  const [openTier, setOpenTier] = useState(null);
  return (
    <div style={{ marginTop: 12, marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: 0.5, color: "#64748b", marginBottom: 6 }}>
        Process effectiveness · last 12 months
      </div>
      {metrics.map((m) => {
        const row = effData?.[m.module]?.[branch] || null;
        const pct = row ? row[m.field] : null;
        const threshold = row ? row[m.thresholdField] : null;
        const band = pctBand(pct, threshold ?? 90);
        const isOpen = openTier === m.id;
        return (
          <div key={m.id} style={{ border: isOpen ? "2px solid #000" : "1px solid #e2e8f0", borderRadius: 8, marginBottom: 8, overflow: "hidden" }}>
            <button
              onClick={() => setOpenTier(isOpen ? null : m.id)}
              style={{
                width: "100%", textAlign: "left", cursor: "pointer", border: "none",
                background: "#fff", padding: "10px 12px", display: "flex",
                alignItems: "center", gap: 12,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{m.label}</span>
                  <HelpInfo help={m.help} />
                </div>
                {/* Bar */}
                <div style={{ marginTop: 6, height: 10, background: "#f1f5f9", borderRadius: 999, overflow: "hidden", position: "relative" }}>
                  <div style={{
                    width: pct == null ? "0%" : `${Math.max(2, Math.min(100, pct))}%`,
                    height: "100%", background: band.fg, borderRadius: 999,
                  }} />
                  {threshold != null && (
                    <div title={`Target ${threshold}%`} style={{
                      position: "absolute", top: -2, bottom: -2, left: `${threshold}%`,
                      width: 2, background: "#0f172a", opacity: 0.5,
                    }} />
                  )}
                </div>
                <div style={{ marginTop: 3, fontSize: 11, color: "#64748b" }}>
                  Target {threshold != null ? `${threshold}%` : "—"} · {band.label}
                  {row && row.monthsWithData != null && ` · ${row.monthsWithData} mo data`}
                </div>
              </div>
              <span style={{
                fontSize: 18, fontWeight: 800, color: band.fg, minWidth: 56, textAlign: "right",
              }}>
                {pct == null ? "—" : `${Math.round(pct)}%`}
              </span>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>{isOpen ? "▾" : "▸"}</span>
            </button>
            {isOpen && <TierBreakdown row={row} metric={m} pct={pct} threshold={threshold} token={token} />}
          </div>
        );
      })}
    </div>
  );
}

// The four-tier maturity drill-down for one metric, plus a per-sub-unit
// month-by-month history table (the Head-Office-style detail).
function TierBreakdown({ row, metric, pct, threshold, token }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Fetch the monthly detail for this pharmacy + module on first expand.
  useEffect(() => {
    let cancelled = false;
    const pid = row?.pharmacyId;
    if (!pid) return;
    setLoading(true); setErr("");
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/headoffice/${withOrgScope(`compliance/effectiveness-detail?module=${metric.module}&pharmacyId=${pid}&months=12`)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setDetail(json);
      } catch (e) { if (!cancelled) setErr(String(e.message || e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [row, metric.module, token]);

  const tiers = [
    { k: "Ever used", v: row ? (row.everUsed ? "Yes" : "No") : "—",
      ok: !!row?.everUsed,
      note: "Has this tool ever been used (any data in the last 12 months)?" },
    { k: "Used recently", v: row ? (row.usedRecently ? "Yes" : "No") : "—",
      ok: !!row?.usedRecently,
      note: "Used in the current or previous month?" },
    { k: "12-month rate", v: pct == null ? "—" : `${Math.round(pct)}%`,
      ok: pct != null && threshold != null && pct >= threshold,
      note: `Process working over the year vs the ${threshold ?? "—"}% target.` },
  ];

  // Which percentage field of the monthly cells this metric is about.
  const monthField = metric.field === "pctInRangeYear" ? "inRangePct" : "onTimePct";

  return (
    <div style={{ background: "#0f172a", padding: "10px 14px" }}>
      {tiers.map((t, i) => (
        <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
          <span style={{ width: 16, color: t.ok ? "#86efac" : "#fca5a5", fontWeight: 700 }}>
            {t.ok ? "✓" : "✗"}
          </span>
          <span style={{ minWidth: 110, color: "#e2e8f0", fontSize: 12, fontWeight: 600 }}>{t.k}</span>
          <span style={{ minWidth: 50, color: t.ok ? "#86efac" : "#fca5a5", fontSize: 12, fontWeight: 700 }}>{t.v}</span>
          <span style={{ color: "#94a3b8", fontSize: 11 }}>{t.note}</span>
        </div>
      ))}

      {/* Detail section: monthly history for fridge/cleaning/date-checking;
          for SOP metrics it is the underlying records themselves. */}
      <div style={{ marginTop: 10, borderTop: "1px solid #1e293b", paddingTop: 8 }}>
        <div style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
          {metric.module === "sop"
            ? (metric.id === "sop-reviewed" ? "SOP register — version & planned review dates" : "Assignment grid — staff × SOP")
            : "Monthly history"}
        </div>
        {loading ? (
          <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading…</div>
        ) : err ? (
          <div style={{ color: "#fca5a5", fontSize: 12 }}>Couldn’t load detail: {err}</div>
        ) : detail?.sop && metric.id === "sop-reviewed" ? (
          // SOP register: every live SOP, its version date and planned review.
          (detail.sop.sops || []).length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 12 }}>No SOPs recorded at this pharmacy.</div>
          ) : (
          <div style={{ overflowY: "auto", maxHeight: 280 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 11, color: "#e2e8f0", width: "100%" }}>
              <thead>
                <tr>
                  {["SOP", "Category", "Version date", "Planned review", "Status"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "4px 8px", position: "sticky", top: 0,
                                         background: "#0f172a", color: "#94a3b8", fontWeight: 700,
                                         textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detail.sop.sops.map((x, i) => (
                  <tr key={i} style={{ borderTop: "1px solid rgba(148,163,184,0.2)" }}>
                    <td style={{ padding: "4px 8px" }}>{x.title}</td>
                    <td style={{ padding: "4px 8px", color: "#94a3b8" }}>{x.category || "—"}</td>
                    <td style={{ padding: "4px 8px" }}>{x.versionDate ? new Date(x.versionDate).toLocaleDateString("en-GB") : "—"}</td>
                    <td style={{ padding: "4px 8px" }}>{x.reviewDate ? new Date(x.reviewDate).toLocaleDateString("en-GB") : "—"}</td>
                    <td style={{ padding: "4px 8px", fontWeight: 700,
                                 color: x.inDate ? "#4ade80" : "#f87171" }}>
                      {x.inDate ? "In date" : "Review overdue"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )
        ) : detail?.sop && metric.id === "sop-read" ? (
          // SOP × staff MATRIX — the same grid format as the SOP compliance
          // grid used elsewhere on the site: one row per SOP, one column per
          // staff member, each cell that person's sign-off state.
          (detail.sop.assignments || []).length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 12 }}>No live SOP assignments at this pharmacy.</div>
          ) : (() => {
            const assignments = detail.sop.assignments;
            const staffNames = [...new Set(assignments.map(a => a.staffName))].sort((a, b) => a.localeCompare(b));
            const sopTitles = [...new Set(assignments.map(a => a.sopTitle))].sort((a, b) => a.localeCompare(b));
            const cell = new Map(); // "sop||staff" -> assignment
            for (const a of assignments) cell.set(`${a.sopTitle}||${a.staffName}`, a);
            const fmt = (d) => d ? new Date(d).toLocaleDateString("en-GB") : "—";
            const CELL_STATES = {
              signed:  { sym: "✓", color: "#4ade80", label: "Signed" },
              pending: { sym: "●", color: "#fbbf24", label: "Pending" },
              overdue: { sym: "✗", color: "#f87171", label: "Overdue" },
            };
            return (
              <>
                <div style={{ display: "flex", gap: 14, fontSize: 10.5, color: "#94a3b8", marginBottom: 6 }}>
                  {Object.values(CELL_STATES).map(st => (
                    <span key={st.label}><span style={{ color: st.color, fontWeight: 700 }}>{st.sym}</span> {st.label}</span>
                  ))}
                  <span>— Not assigned</span>
                </div>
                <div style={{ overflow: "auto", maxHeight: 280 }}>
                  <table style={{ borderCollapse: "collapse", fontSize: 11, color: "#e2e8f0" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: "4px 8px", position: "sticky", top: 0, left: 0,
                                     zIndex: 3, background: "#0f172a", color: "#94a3b8", fontWeight: 700,
                                     textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}>SOP</th>
                        {staffNames.map(sn => (
                          <th key={sn} title={sn}
                              style={{ padding: "4px 6px", position: "sticky", top: 0, zIndex: 2,
                                       background: "#0f172a", color: "#94a3b8", fontWeight: 700,
                                       fontSize: 10, maxWidth: 76, overflow: "hidden",
                                       textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "center" }}>
                            {sn}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sopTitles.map(st => (
                        <tr key={st} style={{ borderTop: "1px solid rgba(148,163,184,0.2)" }}>
                          <td title={st}
                              style={{ padding: "4px 8px", position: "sticky", left: 0, zIndex: 1,
                                       background: "#0f172a", maxWidth: 190, overflow: "hidden",
                                       textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{st}</td>
                          {staffNames.map(sn => {
                            const a = cell.get(`${st}||${sn}`);
                            if (!a) return (
                              <td key={sn} style={{ textAlign: "center", color: "#475569", padding: "3px 6px" }}>—</td>
                            );
                            const stx = CELL_STATES[a.status] || CELL_STATES.pending;
                            return (
                              <td key={sn}
                                  title={`${sn} — ${st}
${stx.label}
Assigned ${fmt(a.assignedAt)} · due ${fmt(a.dueDate)}${a.signedAt ? ` · signed ${fmt(a.signedAt)}` : ""}`}
                                  style={{ textAlign: "center", padding: "3px 6px",
                                           color: stx.color, fontWeight: 800, cursor: "default" }}>
                                {stx.sym}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()
        ) : !detail || !Array.isArray(detail.units) || !detail.units.length ? (
          <div style={{ color: "#94a3b8", fontSize: 12 }}>{detail?.note || "No monthly data."}</div>
        ) : (
          <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 260 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 11, color: "#e2e8f0" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "2px 8px", color: "#94a3b8", position: "sticky", left: 0, top: 0, zIndex: 3, background: "#0f172a" }}>
                    {detail.activityBased ? "Activity" : (metric.module === "fridge" ? "Fridge" : "Area")}
                  </th>
                  {detail.monthKeys.slice().reverse().map((mk) => (
                    <th key={mk} style={{ padding: "2px 6px", color: "#94a3b8", whiteSpace: "nowrap", fontWeight: 600, position: "sticky", top: 0, zIndex: 2, background: "#0f172a" }}>
                      {mk.slice(2)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detail.units.map((u) => {
                  const cells = {};
                  u.months.forEach((m) => { cells[m.monthKey] = m; });
                  return (
                    <tr key={u.unitId}>
                      <td style={{ padding: "2px 8px", whiteSpace: "nowrap", position: "sticky", left: 0, background: "#0f172a" }}>{u.unitName}</td>
                      {detail.monthKeys.slice().reverse().map((mk) => {
                        const c = cells[mk];
                        if (detail.activityBased) {
                          // Activity months: colour by active/inactive, show check count.
                          const active = c ? c.active : false;
                          const checks = c ? c.checksPerformed : 0;
                          return (
                            <td key={mk}
                                title={c ? `${c.checksPerformed} checks · ${c.uniqueAreasChecked} areas · ${c.drugsLogged} drugs logged` : "No data"}
                                style={{ padding: "2px 6px", textAlign: "center", fontWeight: 600,
                                         background: active ? "#16633433" : "#991b1b33",
                                         color: active ? "#86efac" : "#fca5a5" }}>
                              {active ? checks : "·"}
                            </td>
                          );
                        }
                        const v = c ? c[monthField] : null;
                        const bg = v == null ? "transparent"
                          : v >= (threshold ?? 90) ? "#16633433"
                          : v >= (threshold ?? 90) - 10 ? "#854d0e33" : "#991b1b33";
                        const fg = v == null ? "#475569"
                          : v >= (threshold ?? 90) ? "#86efac"
                          : v >= (threshold ?? 90) - 10 ? "#fde68a" : "#fca5a5";
                        return (
                          <td key={mk} style={{ padding: "2px 6px", textAlign: "center", background: bg, color: fg, fontWeight: 600 }}>
                            {v == null ? "·" : `${Math.round(v)}`}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ color: "#64748b", fontSize: 10, marginTop: 4 }}>
              {detail.activityBased
                ? "Each cell = number of date-checks done that month (green = active, red = none). Hover for detail. Newest on the right."
                : `Values are ${metric.field === "pctInRangeYear" ? "% of readings in range" : "% done on time"} per month. Newest on the right.`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// Tag marking an element/item as backed by real live data.
function LiveTag() {
  return (
    <span title="Backed by live data from a Head Office module"
      style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
               padding: "1px 5px", borderRadius: 4, background: "#dcfce7", color: "#166534",
               border: "1px solid #86efac", whiteSpace: "nowrap" }}>
      ● Live
    </span>
  );
}

// Tag marking an element as still on placeholder data.
function PlaceholderTag() {
  return (
    <span title="Placeholder data — not yet wired to a real source"
      style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
               padding: "1px 5px", borderRadius: 4, background: "#f1f5f9", color: "#94a3b8",
               border: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
      Placeholder
    </span>
  );
}

// Small rounded % pill used on item rows.
function PctPill({ pct }) {
  return (
    <span
      style={{
        minWidth: 48, textAlign: "center", padding: "3px 8px",
        borderRadius: 999, background: pctBg(pct), border: "1px solid rgba(0,0,0,0.08)",
        fontWeight: 800, fontSize: 13, color: "#0f172a",
      }}
    >
      {fmtPct(pct)}
    </span>
  );
}

// Legend for the requirement badges + colour meaning.
function Legend() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 11, color: "#475569", flexWrap: "wrap" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <span style={{ width: 14, height: 14, borderRadius: 3, background: pctBg(100), border: "1px solid #cbd5e1" }} /> 100%
        <span style={{ width: 14, height: 14, borderRadius: 3, background: pctBg(50), border: "1px solid #cbd5e1", marginLeft: 6 }} /> 50%
        <span style={{ width: 14, height: 14, borderRadius: 3, background: pctBg(5), border: "1px solid #cbd5e1", marginLeft: 6 }} /> 0%
      </span>
      <span style={{ display: "inline-flex", gap: 6 }}>
        <ReqBadge requirement="mandatory" />
        <ReqBadge requirement="recommended" />
        <ReqBadge requirement="optional" />
      </span>
    </div>
  );
}